import React, { useState, useEffect, useRef, useContext, createContext, useCallback } from 'react';
import { getAuthHeaders } from '../supabaseClient';
import { usePDF } from 'react-to-pdf';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { buildEvidence } from './evidence';
import { computePotential, potentialHeadline, potentialBasisLine } from './heroSelection';
import { assignOverlapRoles } from '../overlapGroups';
import { buildNotesChecklist, groupLabel } from '../lineItemNotes';
import { computeReadQuality, notReadCopy, countTmScheduleEntries } from './readQuality';
import FeedbackWidget from './FeedbackWidget';
import Icon from './Icon';

const SEVERITY_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3 };

// Build identifiers for feedback auto-capture (best-effort; null when the env
// var isn't wired by the host). Vercel exposes the *_VERCEL_GIT_* vars only when
// explicitly mapped into the CRA build, so these gracefully fall back to null.
const RELEASE_ID = process.env.REACT_APP_RELEASE || process.env.REACT_APP_VERCEL_GIT_COMMIT_REF || null;
const COMMIT_ID = process.env.REACT_APP_COMMIT_SHA || process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || null;

// Phase R1 — group findings by their normalized room_context for the optional
// "By Room" view. Attribution/display only; never alters findings.
// Real rooms sort first (alphabetical); the two non-room buckets always sort
// AFTER them so the actual rooms lead and estimate-wide items never dominate.
// A finding that touches several rooms is filed under EACH of them (a true
// per-room walkthrough), so there is no longer a "Multiple rooms" bucket.
const ROOM_GROUP_SPECIAL_ORDER = { 'Whole estimate': 2, 'Unassigned': 3 };

// Owner-facing headers for the non-room buckets. Real rooms keep their own name.
const ROOM_GROUP_DISPLAY = {
    'Whole estimate': 'Applies to the whole estimate',
    'Unassigned': 'Not linked to a specific room',
};
// A short caption under the non-room bucket headers so the grouping is self-explanatory.
const ROOM_GROUP_CAPTION = {
    'Whole estimate': 'Estimate-wide findings that are not tied to any single room.',
    'Unassigned': 'Audee could not tie these to a specific room.',
};

// The real room(s) a finding should be filed under, or null when it belongs in a
// non-room bucket. Uses the room_context.rooms list the engine attaches (all the
// distinct rooms the finding actually touches).
function findingRoomLabels(finding) {
    const rc = finding.room_context;
    if (!rc) return null;
    if (Array.isArray(rc.rooms) && rc.rooms.length > 0) return rc.rooms;
    // Defensive: a single-room exact/derived context without a rooms array.
    if ((rc.confidence === 'exact' || rc.confidence === 'derived')
        && rc.label && rc.label !== 'Multiple rooms') {
        return [rc.label];
    }
    return null;
}

// Location chip text: name the actual rooms a finding touches (up to a few) so
// the room is prominent, instead of a generic "Multiple rooms".
function locationChipText(rc) {
    if (!rc) return null;
    const rooms = Array.isArray(rc.rooms) ? rc.rooms : [];
    if (rooms.length > 1) {
        const shown = rooms.slice(0, 3).join(', ');
        return rooms.length > 3 ? `${shown} +${rooms.length - 3} more` : shown;
    }
    return rc.label;
}

function groupFindingsByRoom(findings) {
    const groups = new Map();
    const add = (label, f) => {
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label).push(f);
    };
    findings.forEach(f => {
        const rooms = findingRoomLabels(f);
        if (rooms) {
            // File the finding under every real room it touches.
            rooms.forEach(r => add(r, f));
        } else {
            const rc = f.room_context;
            add(rc && rc.confidence === 'estimate_wide' ? 'Whole estimate' : 'Unassigned', f);
        }
    });
    // Exact room labels first (alphabetical), then Whole estimate → Unassigned.
    return [...groups.entries()].sort((a, b) => {
        const aSpecial = ROOM_GROUP_SPECIAL_ORDER[a[0]] || 0;
        const bSpecial = ROOM_GROUP_SPECIAL_ORDER[b[0]] || 0;
        if (aSpecial !== bSpecial) return aSpecial - bSpecial;
        return a[0].localeCompare(b[0]);
    });
}

// Phase E1 — Evidence Drawer. Collapsed-by-default, presentation-only view of
// the evidence the rule engine already recorded on this finding. Reads
// existing triggering_data / room_context exclusively; renders nothing when no
// evidence is available.
function EvidenceTable({ table }) {
    return (
        <table className="evidence-table">
            <thead>
                <tr>
                    {table.columns.map((c, i) => <th key={i}>{c}</th>)}
                </tr>
            </thead>
            <tbody>
                {table.rows.map((row, r) => (
                    <tr key={r}>
                        {row.map((cell, c) => <td key={c}>{cell}</td>)}
                    </tr>
                ))}
                {table.more > 0 && (
                    <tr className="evidence-table-more">
                        <td colSpan={table.columns.length}>+{table.more} more</td>
                    </tr>
                )}
            </tbody>
        </table>
    );
}

function EvidenceDrawer({ finding, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    const evidence = buildEvidence(finding);
    if (!evidence) return null;

    const { roomEvidence, lineItems, metrics, fallback } = evidence;

    return (
        <div className="evidence-drawer">
            <button
                type="button"
                className="evidence-toggle"
                aria-expanded={open}
                onClick={() => setOpen(o => !o)}
            >
                <span className="evidence-caret"><Icon name={open ? 'chevron-down' : 'chevron-right'} size={13} /></span>
                {open ? 'Hide evidence' : 'View evidence'}
            </button>
            {open && (
                <div className="evidence-body">
                    {roomEvidence && (
                        <div className="evidence-section">
                            <span className="evidence-section-label">Room evidence</span>
                            {roomEvidence.context && (
                                <div className="evidence-row">
                                    <span className="evidence-row-key">Room context</span>
                                    <span className="evidence-row-val">{roomEvidence.context}</span>
                                </div>
                            )}
                            {roomEvidence.groups.map((g, idx) => (
                                <div key={idx} className="evidence-row">
                                    <span className="evidence-row-key">{g.label}</span>
                                    <span className="evidence-row-val">{g.value}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {lineItems && (
                        <div className="evidence-section">
                            <span className="evidence-section-label">Supporting line items</span>
                            {lineItems.map((group, gIdx) => (
                                <div key={gIdx} className="evidence-table-group">
                                    {lineItems.length > 1 && (
                                        <span className="evidence-item-group-label">{group.caption}</span>
                                    )}
                                    <EvidenceTable table={group.table} />
                                </div>
                            ))}
                        </div>
                    )}
                    {metrics && (
                        <div className="evidence-section">
                            <span className="evidence-section-label">Quantities / measurements</span>
                            <div className="evidence-metrics">
                                {metrics.map((m, idx) => (
                                    <div key={idx} className="evidence-metric">
                                        <span className="evidence-metric-label">{m.label}</span>
                                        <span className="evidence-metric-value">{m.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {fallback && (
                        <div className="evidence-section">
                            <span className="evidence-section-label">Additional data</span>
                            <div className="evidence-grid">
                                {fallback.map((row, idx) => (
                                    <div key={idx} className="evidence-row">
                                        <span className="evidence-row-key">{row.label}</span>
                                        <span className="evidence-row-val">{row.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Calibration: map a confidence label to a CSS modifier for the strength badge.
function strengthClass(label) {
    switch (label) {
        case 'Certain': return 'strength-certain';
        case 'Standard Practice': return 'strength-standard';
        case 'Scope Dependent': return 'strength-scope';
        case 'Investigate': return 'strength-investigate';
        default: return 'strength-standard';
    }
}

// Commercial QA helpers — the single source of truth is the server-computed
// finding.commercialQa block, which is what gates a dollar into the headline.
//
// FAIL CLOSED: a finding with NO commercialQa block is un-gated — its dollar has
// passed no grounding / precision / registration check — so it contributes NOTHING
// to the headline/Verified total. This is the same fail-closed stance HistoryScreen
// already takes (value_tiers null → '—', never the raw dollar_impact). It matters
// because T&M (SOC/Invoice) findings carry no QA block — the T&M engine was never
// wired into Commercial QA — so the old `return f.dollar_impact` fallback would have
// headlined ungated T&M dollars (~$449k on one SOC, $5.1M on a single TM-070) the
// moment those audits were restored. Untiered dollars are never presented as
// recoverable revenue. The finding still renders in full (see plainAmount, which
// shows its own dollar_impact neutrally); it just never sums into a Verified total.
function qaHeadlineAmount(f) {
    if (f.commercialQa) return f.commercialQa.headlineAmount || 0;
    return 0;
}
function qaIsConditional(f) {
    return !!(f.commercialQa && !f.commercialQa.headlineEligible && (f.commercialQa.conditionalAmount || 0) > 0);
}
// Commercial Outcomes tier helpers. The server stamps finding.commercialOutcome
// (VERIFIED / LIKELY / CONDITIONAL). LIKELY = a directly-evidenced quantity whose
// only gap is an approved unit rate (routinely billable). It is a distinct tier —
// NEVER summed into Verified — and it carves the old single "conditional" bucket
// into two, so likely and conditional never overlap.
function outcomeTier(f) {
    return (f.commercialOutcome && f.commercialOutcome.tier) || null;
}
function qaIsLikely(f) {
    return outcomeTier(f) === 'LIKELY' && (f.commercialOutcome.likelyAmount || 0) > 0;
}
// A finding is "purely conditional" (needs docs/vendor/scope) when it is non-headline,
// has a theoretical amount, and is NOT in the Likely tier.
function qaIsPureConditional(f) {
    return qaIsConditional(f) && !qaIsLikely(f);
}
const CONFIDENCE_LABEL = {
    VERY_HIGH: 'Very high confidence', HIGH: 'High confidence', MEDIUM: 'Medium confidence',
    LOW: 'Low confidence', VERY_LOW: 'Very low confidence',
};

// ─── REVIEWER-ONLY: the single shared predicate (display only) ───────────────
// A finding is reviewer-only — muted on its card, and excluded from the
// customer-facing value tiles/counts — when it is suppressed OR tagged
// visibility='reviewer', with ONE carve-out: "Prepare to Explain" findings.
//
// The carve-out matters because the engine tags Prepare-to-Explain findings
// visibility='reviewer' as a by-product of their "Investigate" confidence label
// (evidence strength), NOT as an instruction to hide them from the contractor —
// see the AUDIENCE SEMANTICS DECISION note in FindingCard. So the exclusion is a
// COMPOUND rule: report_section alone cannot express it (a reviewer-only item
// like XACT-FEE002 sits in section 'Opportunity'), and visibility alone would
// wrongly mute the Prepare-to-Explain section.
//
// Dismissal is per-view state, so call sites keep their own !dismissedIds check.
function isExplainSection(f) {
    return f.report_section === 'Prepare to Explain' && !f.suppressed;
}
function isReviewerOnly(f) {
    return !!f.suppressed || (f.visibility === 'reviewer' && !isExplainSection(f));
}
// Documentation categories needed across the conditional set, for the count-based
// customer display (used when the aggregate conditional dollar is withheld).
function qaConditionalCategories(conditionalFindings) {
    const cats = new Set();
    for (const f of conditionalFindings) {
        const b = f.commercialQa && f.commercialQa.amountBasis;
        if (b === 'DOCUMENTATION_DEPENDENT') cats.add('supporting documentation (tickets / invoices / measured quantity)');
        else if (b === 'PERCENT_OF_ESTIMATE') cats.add('vendor or professional pricing');
        else if (b === 'ALLOWANCE_OR_BID_ITEM' || b === 'PLACEHOLDER_DERIVED') cats.add('a vendor quote or invoice');
        else if (b === 'SCOPE_DEPENDENT') cats.add('scope confirmation');
        else cats.add('actual quantity and pricing confirmation');
    }
    return [...cats];
}

// Customer-facing note explaining WHY a monetized finding is not in the headline
// revenue total, plus its documentation checklist. Only renders for conditional
// findings (headline-eligible findings need no explanation).
function CommercialQaNote({ finding }) {
    const qa = finding.commercialQa;
    if (!qa || qa.headlineEligible) return null;
    if (finding.suppressed) return null; // suppressed findings are handled elsewhere
    const docs = qa.documentationRequirements || [];
    return (
        <div className="finding-commercial-qa">
            <span className="commercial-qa-tag">{qa.customerDisposition}</span>
            {qa.exclusionReason && <span className="commercial-qa-reason">{qa.exclusionReason}</span>}
            {docs.length > 0 && (
                <ul className="commercial-qa-docs">
                    {docs.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
            )}
            <span className="commercial-qa-note">
                Excluded from Potential Revenue until verified.
            </span>
        </div>
    );
}

// ── OBJECTIVE 4: per-finding outcome capture ────────────────────────────────
// estimate_learning_outcomes had never received a row: Audee could say "this rule
// found $14,335 of floor protection" but never "and the carrier paid it". This is the
// contractor-facing half of the write path — one lightweight control per finding.
//
// Delivered via context rather than props: FindingCard has six call sites, and
// threading auditId/outcomes/save through all of them would churn every one. The
// provider also means a report rendered outside a saved audit (a fresh, not-yet-saved
// run) simply gets a null auditId and renders no capture UI, with no extra branching.
const OutcomeContext = createContext({ auditId: null, outcomes: {}, saveOutcome: null, busy: {} });

const OUTCOME_CHOICES = [
    { value: 'pursued',            label: 'Added to estimate', hint: 'You included it — no supplement filed yet.' },
    { value: 'submitted',          label: 'Supplement sent',   hint: 'Filed with the carrier, awaiting a response.' },
    { value: 'approved',           label: 'Approved',          hint: 'Carrier paid it in full.' },
    { value: 'partially_approved', label: 'Partially approved',hint: 'Carrier paid some of it.' },
    { value: 'denied',             label: 'Denied',            hint: 'Carrier refused it.' },
    { value: 'not_pursued',        label: "Didn't pursue",     hint: 'Just as useful to Audee as a win.' },
];
// Statuses where the money actually matters. Asking for "$ approved" on a finding the
// contractor never pursued is noise.
const NEEDS_REQUESTED = new Set(['submitted', 'approved', 'partially_approved', 'denied']);
const NEEDS_APPROVED = new Set(['approved', 'partially_approved']);

function OutcomeCapture({ finding }) {
    const { auditId, outcomes, saveOutcome, busy } = useContext(OutcomeContext);
    const existing = outcomes[finding.rule_id] || null;
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState(existing?.outcome_status || '');
    const [requested, setRequested] = useState(existing?.amount_requested ?? '');
    const [approved, setApproved] = useState(existing?.amount_approved ?? '');
    const [notes, setNotes] = useState(existing?.adjuster_notes || '');
    const [error, setError] = useState(null);

    // A saved outcome arriving after mount (the report loads them async) must populate
    // the form, or a contractor would see an empty control for an outcome they recorded.
    useEffect(() => {
        if (!existing) return;
        setStatus(existing.outcome_status || '');
        setRequested(existing.amount_requested ?? '');
        setApproved(existing.amount_approved ?? '');
        setNotes(existing.adjuster_notes || '');
    }, [existing]);

    // No saved audit (a fresh run) → nothing to attach an outcome to.
    if (!auditId || !saveOutcome) return null;
    const saving = !!busy[finding.rule_id];

    const submit = async () => {
        setError(null);
        const res = await saveOutcome(finding.rule_id, {
            outcome_status: status,
            amount_requested: NEEDS_REQUESTED.has(status) ? requested : null,
            amount_approved: NEEDS_APPROVED.has(status) ? approved : null,
            adjuster_notes: notes,
        });
        if (!res.ok) setError(res.error || 'Could not save.');
        else setOpen(false);
    };

    const summary = existing
        ? (OUTCOME_CHOICES.find(c => c.value === existing.outcome_status)?.label || existing.outcome_status)
        : null;

    return (
        <div className="finding-outcome">
            <button
                className={`finding-outcome-toggle${existing ? ' recorded' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
            >
                {existing
                    ? <><Icon name="check" size={14} /> Outcome: {summary}</>
                    : 'What happened with this?'}
                <span className="finding-outcome-chevron"><Icon name={open ? 'chevron-down' : 'chevron-right'} size={11} /></span>
            </button>

            {open && (
                <div className="finding-outcome-form">
                    <div className="finding-outcome-help">
                        Telling Audee the real result — win or loss — is what teaches it which
                        findings are worth your time. Nothing here is shared with the carrier.
                    </div>
                    <div className="finding-outcome-choices">
                        {OUTCOME_CHOICES.map(c => (
                            <button
                                key={c.value}
                                title={c.hint}
                                className={`finding-outcome-choice${status === c.value ? ' selected' : ''}`}
                                onClick={() => setStatus(c.value)}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>

                    {NEEDS_REQUESTED.has(status) && (
                        <div className="finding-outcome-amounts">
                            <label>
                                <span>Amount requested</span>
                                <input type="number" min="0" step="0.01" value={requested}
                                    placeholder={finding.dollar_impact ? String(finding.dollar_impact) : '0.00'}
                                    onChange={e => setRequested(e.target.value)} />
                            </label>
                            {NEEDS_APPROVED.has(status) && (
                                <label>
                                    <span>Amount approved</span>
                                    <input type="number" min="0" step="0.01" value={approved}
                                        onChange={e => setApproved(e.target.value)} />
                                </label>
                            )}
                        </div>
                    )}

                    {status && (
                        <label className="finding-outcome-notes">
                            <span>Adjuster note (optional)</span>
                            <textarea rows={2} value={notes} maxLength={4000}
                                placeholder="What did the adjuster actually say?"
                                onChange={e => setNotes(e.target.value)} />
                        </label>
                    )}

                    {error && <div className="finding-outcome-error">{error}</div>}

                    <div className="finding-outcome-actions">
                        <button className="finding-outcome-save" disabled={!status || saving} onClick={submit}>
                            {saving ? 'Saving…' : existing ? 'Update outcome' : 'Save outcome'}
                        </button>
                        <button className="finding-outcome-cancel" onClick={() => setOpen(false)}>Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Report density (Simple / Pro) ───────────────────────────────────────────
// Presentation-only view state. "Simple" (default) is the owner view: clean
// cards, plain language, one accent, jargon behind a Details disclosure. "Pro"
// reveals the technical detail inline (rule ids, scores, evidence expanded,
// adjuster wording, monetization basis). Delivered via context so FindingCard's
// several call sites don't each thread a prop. See docs/design/AUDEE-DESIGN-SYSTEM.md
// "Report & finding-card patterns".
const DensityContext = createContext('simple');

// Plain-English tier for a finding — the single source of truth for a card's ONE
// accent color, its tier chip, and its right-side amount/pill. Maps the internal
// report_section + Commercial-Outcomes tier onto the owner-facing vocabulary:
//   verified   → green  "Ready to bill"
//   likely     → amber  "Review first"  (routinely billable once the rate is confirmed)
//   conditional→ amber  "Review first"  (needs docs / vendor pricing / scope)
//   correction → red    "Fix before sending"
//   explain    → blue   "Be ready to explain"
// Attribution only — never changes findings, numbers, or tier math.
function plainTier(f) {
    if (f.report_section === 'Requires Correction') return 'correction';
    if (f.report_section === 'Prepare to Explain') return 'explain';
    if (qaIsLikely(f)) return 'likely';
    if (qaIsPureConditional(f)) return 'conditional';
    return 'verified';
}
const PLAIN_TIER = {
    verified:    { color: 'verified',   chip: 'Ready to bill',        pill: null },
    likely:      { color: 'likely',     chip: 'Review first',         pill: 'Review first' },
    conditional: { color: 'likely',     chip: 'Review first',         pill: 'Review first' },
    correction:  { color: 'correction', chip: 'Fix before sending',   pill: 'Fix before sending' },
    explain:     { color: 'explain',    chip: 'Be ready to explain',  pill: 'Be ready to explain' },
};
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

// GAP-091 ruling 4 (founder, 2026-10-30): a dollar built on a rate Audee assumes —
// not one printed on this estimate — says so, in these words, wherever the finding
// shows its own figure. Document-priced dollars (billed totals, the estimate's own
// prices) carry no such note. The server stamps amount_basis on every priced
// finding; audits saved before it did fall back to the QA rate source they were
// saved with (a code rate, a percentage, or a typical-rate heuristic).
const TYPICAL_RATE_NOTE = 'Estimated at a typical rate. Confirm against your price list.';
function typicalRateNote(f) {
    if (!f) return null;
    if (f.amount_basis) return f.amount_basis === 'typical_rate' ? (f.amount_note || TYPICAL_RATE_NOTE) : null;
    const rs = f.commercialQa && f.commercialQa.rateSource;
    return ['HARDCODED_HEURISTIC', 'VALIDATED_CONFIG', 'PERCENT_DERIVED'].includes(rs) ? TYPICAL_RATE_NOTE : null;
}

// The right-side element of a card: a bankable dollar amount (green = recoverable
// money, amber = likely) when there is one, otherwise null so the card shows a
// status pill instead. Reads the same server-computed values the detailed view
// uses — nothing is recomputed.
function plainAmount(f, tier) {
    if (tier === 'likely') {
        const v = (f.commercialOutcome && f.commercialOutcome.likelyAmount) || 0;
        return v > 0 ? { text: money(v), tone: 'likely' } : null;
    }
    if (tier === 'verified') {
        const v = qaHeadlineAmount(f);
        if (v > 0) return { text: money(v), tone: 'money' };
        // Un-gated finding (no commercialQa, e.g. T&M): its dollar is NOT recoverable
        // revenue, so it never wears money-green — but the card still shows the
        // finding's own figure, NEUTRALLY, so the finding renders in full. It is
        // absent from every headline sum because qaHeadlineAmount returned 0 above.
        if (!f.commercialQa && (f.dollar_impact || 0) > 0) return { text: money(f.dollar_impact), tone: 'neutral' };
        return null;
    }
    if (tier === 'correction') {
        // A correction's dollar is an error to fix (often money to REMOVE), not
        // recoverable money — so it wears NEUTRAL dark (--text), never money-green
        // or amber. The red "Fix before sending" accent carries the tier signal.
        const v = qaHeadlineAmount(f);
        if (v > 0) return { text: money(v), tone: 'neutral' };
        // Same fail-closed fallback for un-gated corrections (see 'verified' above).
        if (!f.commercialQa && (f.dollar_impact || 0) > 0) return { text: money(f.dollar_impact), tone: 'neutral' };
        return null;
    }
    if (tier === 'explain') {
        // A Prepare-to-Explain dollar is the amount EXPOSED to adjuster challenge — money
        // the contractor must be ready to justify, NOT recoverable revenue. So it wears
        // NEUTRAL dark (--text), never money-green or amber, and the blue "Be ready to
        // explain" accent carries the tier signal. It is never summed into any headline:
        // ImpactSummary excludes Prepare-to-Explain from every total (explainImpact is a
        // separate, muted figure). Most explain findings are documentation gaps carrying
        // $0 and fall through to the pill; only priced items (e.g. an after-hours premium)
        // surface a figure here.
        if ((f.dollar_impact || 0) > 0) return { text: money(f.dollar_impact), tone: 'neutral' };
        return null;
    }
    return null; // conditional → no bankable dollar, show the pill
}

// The technical metadata row (rule id, severity, confidence + score, reviewer /
// AI labels). Rendered inline in Pro; folded into the Details disclosure in Simple.
function FindingMeta({ finding, reviewerOnly, dismissed }) {
    const sev = finding.severity.toLowerCase();
    return (
        <div className="finding-meta">
            <span className="rule-id">{finding.rule_id}</span>
            <span className={`severity-badge severity-${sev}`}>{finding.severity}</span>
            {finding.reportLanguage && (
                <span className={`confidence-badge ${strengthClass(finding.confidenceLabel)}`}>
                    {finding.reportLanguage}
                    {typeof finding.confidenceScore === 'number' ? ` · ${finding.confidenceScore}` : ''}
                </span>
            )}
            {reviewerOnly && <span className="reviewer-badge">Reviewer only</span>}
            {finding.is_ai_finding && <span className="ai-badge">AI</span>}
            {dismissed && <span className="reviewed-badge">Reviewed</span>}
        </div>
    );
}

// The technical detail block — everything that is jargon or evidence. Collapsed
// behind "Details" in Simple; rendered inline (and evidence auto-expanded) in Pro.
function FindingDetails({ finding, reviewerOnly, dismissed, generating, pro }) {
    const oc = finding.commercialOutcome;
    // Prepare-to-Explain findings are shown at full prominence but are not billable-
    // revenue items, so the outcome-capture tracker below is held off them (see the
    // matching note in FindingCard on the audience/visibility decision).
    const isExplain = isExplainSection(finding);
    return (
        <div className="finding-details">
            {/* In Simple the meta row lives here (behind Details); in Pro it is already
                shown in the header, so it is not duplicated. */}
            {!pro && <FindingMeta finding={finding} reviewerOnly={reviewerOnly} dismissed={dismissed} />}
            {oc && oc.tier !== 'EXCLUDED' && (
                <div className={`outcome-tier-badge outcome-tier-${oc.tier.toLowerCase()}`} title={`${CONFIDENCE_LABEL[oc.commercialConfidence] || ''} · score ${oc.outcomeScore}/100`}>
                    {oc.tierLabel}
                </div>
            )}
            {oc && oc.tier !== 'EXCLUDED' && oc.customerAction && (
                <div className={`finding-next-step finding-next-step-${oc.tier.toLowerCase()}`}>
                    <span className="finding-next-step-label">Recommended next step</span>
                    <span className="finding-next-step-action">{oc.customerAction}</span>
                    {oc.typicalCarrierAcceptance && (
                        <span className="finding-next-step-note">{oc.typicalCarrierAcceptance}</span>
                    )}
                </div>
            )}
            <CommercialQaNote finding={finding} />
            {finding.reviewRecommendation && finding.confidenceLabel !== 'Standard Practice' && finding.confidenceLabel !== 'Certain' && (
                <div className={`finding-review-rec ${strengthClass(finding.confidenceLabel)}`}>
                    {finding.reviewRecommendation}
                </div>
            )}
            {Array.isArray(finding.room_recommendations) && finding.room_recommendations.length > 0 && (
                <div className="room-recommendations">
                    <span className="room-recommendations-label">Room-specific recommendations</span>
                    <ul className="room-recommendations-list">
                        {finding.room_recommendations.map((rec, idx) => (
                            <li key={idx} className="room-recommendation">
                                <span className="room-recommendation-room">{rec.label}</span>
                                {rec.suggested_item && (
                                    <span className="room-recommendation-item">Suggested: {rec.suggested_item}</span>
                                )}
                                <span className="room-recommendation-reason">{rec.reason}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {finding.code_citation && (
                <div className="finding-defensibility">
                    <span className="finding-defensibility-label">Code basis</span>
                    {finding.code_citation}
                </div>
            )}
            {finding.adjuster_rebuttal && (
                <div className="finding-defensibility finding-defensibility-rebuttal">
                    <span className="finding-defensibility-label">If the adjuster pushes back</span>
                    {finding.adjuster_rebuttal}
                </div>
            )}
            {finding.is_ai_finding && finding.ai_reasoning && (
                <div className="ai-reasoning">
                    <span className="ai-reasoning-label">AI reasoning</span>
                    {finding.ai_reasoning}
                </div>
            )}
            {/* Objective 4: outcome capture. Hidden while generating a PDF (it is an
                interactive control, not report content) and on reviewer-only/suppressed
                findings, which were never shown to the customer as actionable. Also held
                off Prepare-to-Explain findings (isExplain): those are now shown at full
                prominence as a defensive checklist, but they are not billable-revenue
                items, so the "What happened with this?" billing-outcome tracker does not
                apply to them. */}
            {!generating && !reviewerOnly && !isExplain && !dismissed && (
                <OutcomeCapture finding={finding} />
            )}
            <EvidenceDrawer finding={finding} defaultOpen={pro} />
        </div>
    );
}

function FindingCard({ finding, onDismiss, onRestore, dismissed, generating }) {
    const density = useContext(DensityContext);
    const pro = density === 'pro';
    // AUDIENCE SEMANTICS DECISION (2026-07-21): a "Prepare to Explain" finding carries
    // audience='adjuster'. That means "about the adjuster RELATIONSHIP" — a documentation
    // gap or line item the adjuster is likely to challenge or cut — NOT "for adjuster eyes
    // only". The person who must act on it is the contractor (our customer): fix it, or be
    // ready to justify it, before the estimate goes out. So these are first-class customer
    // content. (`audience` no longer drives any filtering anywhere in the app — the old
    // All/Contractor/Adjuster filter was retired — so this reading is realized purely by
    // rendering them at full prominence here.)
    //
    // The audit engine also tags them visibility='reviewer', but only as a by-product of
    // their "Investigate" confidence label (evidence strength), NOT an instruction to hide
    // them from the contractor. So Prepare-to-Explain findings are NOT muted like true
    // reviewer-only notes. Suppressed findings still mute regardless of section.
    const reviewerOnly = isReviewerOnly(finding);

    const tier = plainTier(finding);
    const cfg = PLAIN_TIER[tier];
    const chip = cotalityChip(finding) || cfg.chip;
    const amount = plainAmount(finding, tier);
    const amountNote = amount ? typicalRateNote(finding) : null;

    // Simple view keeps the technical detail one tap away; Pro reveals it inline.
    // During PDF export everything renders so the exported report stays complete.
    const [detailsOpen, setDetailsOpen] = useState(false);
    const showDetails = pro || detailsOpen || generating;

    return (
        <div
            id={`finding-${finding.rule_id}`}
            className={`finding-card tier-${cfg.color} density-${density} ${dismissed ? 'dismissed' : ''} ${finding.suppressed ? 'suppressed' : ''} ${reviewerOnly ? 'reviewer-only' : ''}`}
        >
            {/* Pro: the technical metadata row leads the card. Simple hides it. */}
            {pro && (
                <div className="finding-top">
                    <FindingMeta finding={finding} reviewerOnly={reviewerOnly} dismissed={dismissed} />
                </div>
            )}

            <div className="finding-headline">
                <div className="finding-name">{finding.customerLabel || finding.rule_name}</div>
                <div className="finding-headline-right">
                    {amount
                        ? <span className={`finding-amount amount-${amount.tone}`}>{amount.text}</span>
                        : cfg.pill && cfg.pill !== cfg.chip && <span className={`finding-status-pill pill-${cfg.color}`}>{cfg.pill}</span>}
                </div>
            </div>
            {amountNote && <div className="finding-amount-note">{amountNote}</div>}

            <div className="finding-chips">
                <span className={`tier-chip chip-${cfg.color}`}>{chip}</span>
                {finding.room_context?.label && (
                    <span className="location-chip"><Icon name="map-pin" size={13} />{locationChipText(finding.room_context)}</span>
                )}
            </div>

            <div className="finding-fix">{finding.recommended_fix}</div>

            <div className="finding-actions">
                {!pro && !generating && (
                    <button
                        className={`finding-details-toggle${detailsOpen ? ' open' : ''}`}
                        aria-expanded={detailsOpen}
                        onClick={() => setDetailsOpen(o => !o)}
                    >
                        {detailsOpen ? 'Hide details' : 'Details'}
                        <span className="finding-details-caret"><Icon name={detailsOpen ? 'chevron-down' : 'chevron-right'} size={13} /></span>
                    </button>
                )}
                {!generating && (
                    dismissed
                        ? <button className="dismiss-btn restore" onClick={() => onRestore(dismissKey(finding))}>Unclear</button>
                        : <button className="dismiss-btn" onClick={() => onDismiss(dismissKey(finding))}>Clear</button>
                )}
            </div>

            {showDetails && (
                <FindingDetails
                    finding={finding}
                    reviewerOnly={reviewerOnly}
                    dismissed={dismissed}
                    generating={generating}
                    pro={pro}
                />
            )}
        </div>
    );
}

// One card for a Cotality rule that fired in several rooms (see groupCotality).
// Each room row names its lines and has its own Clear, keyed on rule_id + seqs
// (dismissKey), so clearing one room leaves the others on the card. A message the
// rows share reads once; per-room messages read under their room.
function CotalityGroupCard({ findings, onDismiss, onRestore, dismissed, generating }) {
    const density = useContext(DensityContext);
    const pro = density === 'pro';
    const [detailsOpen, setDetailsOpen] = useState(false);
    const showDetails = pro || detailsOpen || generating;

    const first = findings[0];
    const cfg = PLAIN_TIER[plainTier(first)];
    const chip = cotalityChip(first) || cfg.chip;
    const shared = findings.every(f => f.recommended_fix === first.recommended_fix) ? first.recommended_fix : null;
    const allRooms = findings.every(f => f.evidence && f.evidence.room);
    const where = (f) => ((f.evidence && f.evidence.rooms) || []).join(', ') || 'Whole estimate';
    const lines = (f) => {
        const seqs = (f.evidence && f.evidence.seqs) || [];
        return seqs.length ? `${seqs.length > 1 ? 'Lines' : 'Line'} ${seqs.join(', ')}` : '';
    };

    return (
        <div
            id={`finding-${first.rule_id}`}
            className={`finding-card cot-group-card tier-${cfg.color} density-${density} ${dismissed ? 'dismissed' : ''}`}
        >
            {pro && (
                <div className="finding-top">
                    <FindingMeta finding={first} reviewerOnly={false} dismissed={dismissed} />
                </div>
            )}

            <div className="finding-headline">
                <div className="finding-name">{first.customerLabel || first.rule_name}</div>
                <div className="finding-headline-right">
                    <span className="cot-group-count">{findings.length} {allRooms ? 'rooms' : 'places'}</span>
                </div>
            </div>

            <div className="finding-chips">
                <span className={`tier-chip chip-${cfg.color}`}>{chip}</span>
            </div>

            {shared && <div className="finding-fix">{shared}</div>}

            <ul className="cot-group-rows">
                {findings.map(f => (
                    <li key={cardKey(f)} className="cot-group-row">
                        <div className="cot-group-row-head">
                            <span className="cot-group-room"><Icon name="map-pin" size={13} />{where(f)}</span>
                            <span className="cot-group-lines">{lines(f)}</span>
                            {!generating && (
                                dismissed
                                    ? <button className="dismiss-btn restore" onClick={() => onRestore(dismissKey(f))}>Unclear</button>
                                    : <button className="dismiss-btn" onClick={() => onDismiss(dismissKey(f))}>Clear</button>
                            )}
                        </div>
                        {!shared && <div className="cot-group-message finding-fix">{f.recommended_fix}</div>}
                        {showDetails && <EvidenceDrawer finding={f} defaultOpen={pro} />}
                    </li>
                ))}
            </ul>

            {!pro && !generating && (
                <div className="finding-actions">
                    <button
                        className={`finding-details-toggle${detailsOpen ? ' open' : ''}`}
                        aria-expanded={detailsOpen}
                        onClick={() => setDetailsOpen(o => !o)}
                    >
                        {detailsOpen ? 'Hide details' : 'Details'}
                        <span className="finding-details-caret"><Icon name={detailsOpen ? 'chevron-down' : 'chevron-right'} size={13} /></span>
                    </button>
                </div>
            )}

            {showDetails && (
                <div className="finding-details">
                    {!pro && <FindingMeta finding={first} reviewerOnly={false} dismissed={dismissed} />}
                    {/* Outcomes are stored per (audit, rule), so the card has one tracker. */}
                    {!generating && !dismissed && !isExplainSection(first) && <OutcomeCapture finding={first} />}
                </div>
            )}
        </div>
    );
}

// A section's cards: grouped Cotality cards where a rule repeats, one FindingCard
// per finding everywhere else (so Xactimate and T&M render exactly as before).
function FindingCards({ list, onDismiss, onRestore, dismissed, generating }) {
    return (
        <>
            {groupCotality(list).map(g => (g.length > 1
                ? <CotalityGroupCard key={cardKey(g[0])} findings={g} onDismiss={onDismiss} onRestore={onRestore} dismissed={dismissed} generating={generating} />
                : <FindingCard key={cardKey(g[0])} finding={g[0]} onDismiss={onDismiss} onRestore={onRestore} dismissed={dismissed} generating={generating} />))}
        </>
    );
}

// ── Cotality (Symbility) estimates (DEC-012) ─────────────────────────────────
// A Cotality audit is checked against the estimate's own printed numbers. Audee
// holds no Cotality pricing (GAP-078), so its findings never carry a dollar and
// the report has no dollar headline. The backend marks the parse (platform
// 'Symbility') and attaches parsedData.cotality: parse confidence + rule status.
export function isCotalityReport(parsedData) {
    return !!parsedData && (parsedData.platform === 'Symbility' || !!parsedData.cotality);
}

// The parse-confidence warning is up: the read did not reconcile, so an empty
// section means "nothing in the lines Audee read", not "nothing wrong".
const cotalityParseWarning = (parsedData) => !!(parsedData && parsedData.cotality
    && parsedData.cotality.parseConfidence && parsedData.cotality.parseConfidence.status === 'warning');

// Dismissal key (GAP-085). A COT rule fires once per room or line group, so a
// Cotality finding is cleared by rule_id + the seqs it cites. Xactimate and T&M keep
// keying on rule_id, so no existing dismissal changes meaning.
export const dismissKey = (f) => (f.platform === 'cotality'
    ? `${f.rule_id}@${((f.evidence && f.evidence.seqs) || []).join(',')}`
    : f.rule_id);

// The chip on a Cotality addition. "Ready to bill" promises money Audee has not
// priced (GAP-078); a COT addition is something to add if the work was done, and a
// TBD line (COT-O-08) is one to supplement once its cost is known.
const cotalityChip = (f) => {
    if (f.platform !== 'cotality' || f.report_section !== 'Opportunity') return null;
    return f.rule_id === 'COT-O-08' ? 'Supplement when known' : 'Add if performed';
};

// Cotality findings of one rule share a card that lists each room with its lines,
// instead of one card per room (W-09 fires in six rooms on the water fixture). They
// group only within a section, with the same downgrade state and the same title.
// Anything else, and every non-Cotality finding, stays a card of its own. Display
// only: findings, counts and dismissal keys are per finding underneath.
export function groupCotality(list) {
    const groups = new Map();
    list.forEach((f, i) => {
        const key = f.platform === 'cotality'
            ? [f.rule_id, f.report_section, f.category_downgraded_from || '', f.customerLabel || f.rule_name].join('|')
            : `#${i}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(f);
    });
    return [...groups.values()];
}

// Cotality counts name the items and the groups they sit in ("4 groups · 11 items"),
// or just the items when every group holds one ("1 item"). Founder, 2026-10-27.
const itemsText = (n) => `${n} item${n === 1 ? '' : 's'}`;
const groupsText = (n) => `${n} group${n === 1 ? '' : 's'}`;
export function cotalityCountText(list) {
    const groups = groupCotality(list).length;
    return groups === list.length ? itemsText(list.length) : `${groupsText(groups)} · ${itemsText(list.length)}`;
}

// GAP-### ids are internal tracking references, not customer language.
const plainReason = (r) => String(r || '').replace(/\s*\(GAP-\d+\)/g, '').replace(/,\s*GAP-\d+/g, '').trim();

// The Cotality hero: counts per section, never a dollar figure.
function CotalitySummary({ findings, dismissedIds }) {
    const shown = findings.filter(f => !dismissedIds.has(dismissKey(f)) && !f.suppressed);
    const inSection = (section) => shown.filter(f => f.report_section === section);
    // Every item, and the groups (cards) they sit in, so the hero does not
    // understate the work: one rule across six rooms is one card but six items.
    const active = [...inSection('Requires Correction'), ...inSection('Opportunity'), ...inSection('Prepare to Explain')];
    const groups = groupCotality(inSection('Requires Correction')).length
        + groupCotality(inSection('Opportunity')).length + groupCotality(inSection('Prepare to Explain')).length;
    const headline = groups === active.length
        ? `${itemsText(active.length)} to review`
        : `${itemsText(active.length)} to review, in ${groupsText(groups)}`;
    const steps = [
        { key: 'correction', icon: 'wrench', list: inSection('Requires Correction'),
            label: (t) => `Fix ${t}`, desc: 'Correct these before sending the estimate.' },
        { key: 'verified', icon: 'clipboard', list: inSection('Opportunity'),
            label: (t) => `Check ${t}`, desc: 'Items the estimate may be missing. Add them if the work was done.' },
        { key: 'explain', icon: 'shield', list: inSection('Prepare to Explain'),
            label: (t) => `Prepare to explain ${t}`, desc: 'Fix these or be ready to justify them before sending.' },
    ].filter(s => s.list.length > 0);
    return (
        <div className="impact-summary-panel contractor-hero cotality-hero">
            <div className="contractor-hero-main">
                <div className="contractor-hero-eyebrow">Cotality estimate checked</div>
                <div className="contractor-hero-amount contractor-hero-amount-msg">{headline}</div>
                <div className="contractor-hero-sub">
                    Audee checks a Cotality estimate against its own printed numbers and does not price it, so this report has no dollar figure.
                </div>
            </div>
            {steps.length > 0 && (
                <div className="next-steps">
                    <div className="next-steps-title">Next steps</div>
                    <div className="next-steps-list">
                        {steps.map(step => (
                            <div className={`next-step next-step-${step.key}`} key={step.key}>
                                <span className="next-step-icon"><Icon name={step.icon} size={22} /></span>
                                <div className="next-step-body">
                                    <div className="next-step-label">{step.label(cotalityCountText(step.list))}</div>
                                    <div className="next-step-desc">{step.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// Shown when the read did not reconcile with the printed totals, the totals are not
// printed, or many lines went unread. The findings still show; this says how far
// to trust them.
function CotalityParseNotice({ parsedData }) {
    const pc = parsedData.cotality && parsedData.cotality.parseConfidence;
    if (!pc || pc.status !== 'warning') return null;
    return (
        <div className="ocr-disclosure review cotality-parse-notice">
            <Icon name="alert-triangle" size={15} />{' '}
            <b>Parse confidence: check this read.</b>{' '}
            {pc.reasons.map(r => r.message).join(' ')}{' '}
            The findings below are shown, but some may be missing or rest on lines Audee read wrong. Confirm against your source document.
        </div>
    );
}

// Rules that could not run on this estimate, and why. Collapsed by default so the
// report leads with what was found; expanded in the PDF so the export is complete.
function CotalityNotEvaluated({ parsedData, generating }) {
    const [open, setOpen] = useState(false);
    const rules = ((parsedData.cotality && parsedData.cotality.ruleStatus) || []).filter(r => r.status === 'not_evaluated');
    if (!rules.length) return null;
    return (
        <div className="report-section not-evaluated-section">
            <div className="section-header suppressed" onClick={() => !generating && setOpen(o => !o)} style={{ cursor: generating ? 'default' : 'pointer' }}>
                <span className="section-icon"><Icon name="circle-dashed" size={20} /></span>
                Not checked on this estimate
                <span className="section-count">{rules.length}</span>
                {!generating && <span className="section-chevron"><Icon name={open ? 'chevron-up' : 'chevron-down'} size={14} /></span>}
            </div>
            {(open || generating) && (
                <>
                    <div className="suppressed-section-note">
                        These checks need something this estimate does not print, so Audee did not run them. They are not passes.
                    </div>
                    <ul className="not-evaluated-list">
                        {rules.map(r => (
                            <li key={r.id} className="not-evaluated-row">
                                <span className="not-evaluated-name">{r.name}</span>
                                <span className="not-evaluated-id">{r.id}</span>
                                <span className="not-evaluated-reason">Not checked: {plainReason(r.reason)}.</span>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}

// React key for a finding card. Rule ids repeat within a report (a Cotality rule
// fires once per room), so the provenance occurrence id leads; rule_id is the
// fallback for findings saved before provenance existed.
const cardKey = (f) => (f.provenance && f.provenance.finding_uid) || f.rule_id;

function ImpactSummary({ findings, dismissedIds, parsedData, auditId }) {
    // Customer-facing summary. Reviewer-only findings are excluded from every count
    // and tile below so the boxes only ever total what the customer is being asked
    // to act on. That means suppressed findings (dollars zeroed by calibration,
    // shown under "Suppressed / Reviewer Notes") AND visibility='reviewer' notes,
    // which still render as muted reviewer-only cards but must not reach a
    // customer-facing number — e.g. XACT-FEE002 ($2,500 conditional, section
    // 'Opportunity') was inflating the Conditional count while its card was muted.
    // Prepare-to-Explain is deliberately NOT reviewer-only; see isReviewerOnly.
    // Display only: no tier, engine, or dollar value changes — this decides which
    // findings the tiles count, never what any finding is worth.
    const activeFindings = findings
        .filter(f => !dismissedIds.has(dismissKey(f)))
        .filter(f => !isReviewerOnly(f));
    // Commercial QA: the headline sums only HEADLINE-ELIGIBLE amounts. Findings
    // that fired but whose amount is percentage-based, documentation-dependent,
    // scope-dependent, or overlapping are shown separately as "requiring
    // verification" and are NOT added to Potential Revenue.
    const opportunityTotal = activeFindings
        .filter(f => f.report_section === 'Opportunity')
        .reduce((sum, f) => sum + qaHeadlineAmount(f), 0);
    const opportunityCount = activeFindings
        .filter(f => f.report_section === 'Opportunity' && !qaIsConditional(f)).length;
    const correctionImpact = activeFindings
        .filter(f => f.report_section === 'Requires Correction')
        .reduce((sum, f) => sum + qaHeadlineAmount(f), 0);
    // LIKELY (Commercial Outcomes middle tier) — directly-evidenced quantity whose
    // only gap is an approved unit rate. Routinely billable; shown as its own
    // "Likely Recoverable Revenue" total, NEVER summed into Verified.
    const likelyFindings = activeFindings.filter(qaIsLikely);
    const likelyTotal = likelyFindings.reduce((s, f) => s + (f.commercialOutcome.likelyAmount || 0), 0);
    const likelyCount = likelyFindings.length;
    // CONDITIONAL (requires verification) — visible, actionable, but not headline AND
    // not Likely (needs documentation / vendor pricing / scope). No overlap with Likely.
    const conditionalFindings = activeFindings.filter(qaIsPureConditional);
    const conditionalTotal = conditionalFindings.reduce((s, f) => s + (f.commercialQa.conditionalAmount || 0), 0);
    const conditionalCount = conditionalFindings.length;
    // FALSE-PRECISION GUARD: an aggregate conditional dollar is shown to the customer
    // ONLY when EVERY component independently permits a precise defensible amount
    // (commercialQa.showPreciseAmount). If ANY component is percentage-based,
    // documentation-dependent, unknown, a flat heuristic, or an unvalidated rate, the
    // aggregate is HIDDEN — we present the COUNT and the documentation CATEGORIES
    // instead of summing unsupported precise amounts into a fake "up to ~$X" range.
    const conditionalAmountShowable = conditionalCount > 0 &&
        conditionalFindings.every(f => f.commercialQa && f.commercialQa.showPreciseAmount && f.commercialQa.conditionalAmount > 0);
    const conditionalCategories = qaConditionalCategories(conditionalFindings);
    // Batch 1 step 2: RECOVERABLE = Opportunity + Requires Correction ONLY.
    // Prepare-to-Explain dollars are items to justify, never recoverable — they
    // live only in the "Prepare to explain" box, never summed into the headline.
    const explainImpact = activeFindings
        .filter(f => f.report_section === 'Prepare to Explain')
        .reduce((sum, f) => sum + (f.dollar_impact || 0), 0);
    const totalImpact = opportunityTotal + correctionImpact;

    const correctionCount = activeFindings.filter(f => f.report_section === 'Requires Correction').length;
    const explainCount = activeFindings.filter(f => f.report_section === 'Prepare to Explain').length;

    const fmt = (n) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    const plural = (n) => (n === 1 ? '' : 's');


    // Plain-English action rows — only the ones that actually apply. The contractor
    // reads these top-to-bottom, and as of 2026-08-17 that order matches the report's
    // section order: what to ADD and bill first (it is the money), then what to fix,
    // then what to verify or be ready to defend. No severity, no rule IDs, no jargon.
    // The "Prepare to explain" step is the defensive row and is explicitly flagged as
    // NOT revenue. Only the ORDER changed — every row's text, dollar and muting is
    // exactly as before.
    const nextSteps = [];
    if (opportunityCount > 0) {
        nextSteps.push({
            // Verified tier: money grounded in the estimate and billable today. It
            // wears money-green with bill-now language — never Likely's amber or verb.
            key: 'verified',
            icon: 'coins',
            label: `Add ${opportunityCount} item${plural(opportunityCount)} you can bill now`,
            desc: 'These are grounded in the estimate and ready to invoice.',
            amount: opportunityTotal,
        });
    }
    // Likely findings — commercially strong, routinely billed. Real quantity from
    // the estimate; only the unit rate is an Audee assumption a quick vendor quote
    // confirms. Shown as its own recoverable-adjacent step, NEVER summed into Verified.
    if (likelyCount > 0) {
        nextSteps.push({
            key: 'likely',
            icon: 'trending-up',
            label: `Add ${likelyCount} likely opportunit${likelyCount === 1 ? 'y' : 'ies'} — confirm unit pricing`,
            desc: 'Quantity is on the estimate; a quick vendor quote confirms the rate.',
            amount: likelyTotal,
            amountPrefix: '',
        });
    }
    if (correctionCount > 0) {
        nextSteps.push({
            key: 'correction',
            icon: 'wrench',
            label: `Fix ${correctionCount} issue${plural(correctionCount)}`,
            desc: 'Correct these before sending the estimate.',
            amount: correctionImpact,
        });
    }

    // Conditional findings — real issues with a theoretical amount that need
    // documentation, vendor pricing, or scope confirmation before they can be
    // presented as recoverable revenue. Shown muted, NEVER summed into the headline.
    if (conditionalCount > 0) {
        // The aggregate dollar is included ONLY when every component is precise-
        // defensible (conditionalAmountShowable). Otherwise we show the count + the
        // documentation categories — no "up to ~$X" over unsupported amounts.
        const catText = conditionalCategories.length
            ? `Needs ${conditionalCategories.slice(0, 3).join('; ')}.`
            : 'Confirm scope or pricing before adding these.';
        nextSteps.push({
            key: 'conditional',
            icon: 'clipboard',
            label: `Verify ${conditionalCount} opportunit${conditionalCount === 1 ? 'y' : 'ies'} before counting as revenue`,
            desc: catText,
            amount: conditionalAmountShowable ? conditionalTotal : 0,
            amountPrefix: conditionalAmountShowable ? '~' : '',
            muted: true,
        });
    }
    if (explainCount > 0) {
        nextSteps.push({
            key: 'explain',
            icon: 'shield',
            label: `Prepare to explain ${explainCount} item${plural(explainCount)}`,
            desc: 'Fix these or be ready to justify them before sending. Not included in potential revenue.',
            // Shown muted (grey, never amber) so it reads as "be ready to defend",
            // not as money Audee found. This is the ONLY place the explain dollar
            // total surfaces, keeping it clearly separate from potential revenue.
            amount: explainImpact,
            muted: true,
        });
    }

    // ── ONE NUMBER IN THE HERO (2026-08-17) ─────────────────────────────────
    // The Verified / Likely / Conditional pill row is GONE from the hero. It made a
    // reader compare three figures before knowing what the report was worth, and its
    // Verified pill duplicated what the subtitle already says — "including $X you can
    // bill today" carries exactly the same fact in the sentence the reader is already
    // reading. The hero is now the bounded potential and that one subtitle line.
    //
    // THE TIER SYSTEM IS UNCHANGED. rollupTiers, value_tiers, Commercial QA,
    // calibration and the dashboards all compute and store precisely what they did
    // before, and every finding CARD still carries its own tier label. This is a
    // presentation change in one panel, not a change to what the engine believes.

    // POTENTIAL-FIRST (Decision Log 2026-08-13). Display-only: the headline is the
    // bounded POTENTIAL — Verified + Likely + Conditional, less the exclusions in
    // heroSelection.computePotential(). The three amounts passed in are the SAME
    // tier totals rollupTiers computes and the finding cards below are labelled from,
    // so the headline still ties out to the findings underneath it even though the
    // tier PILLS are gone; nothing is re-computed and no finding is re-graded here.
    // conditionalAmountShowable is the existing false-precision gate — when it is
    // false the conditional dollars still count toward the figure, but the figure
    // renders as a floor ("$X+") rather than a precise total.
    const potential = computePotential({
        findings: activeFindings,
        verifiedTotal: totalImpact,
        likelyTotal,
        conditionalTotal,
        grandTotal: parsedData?.grandTotal ?? parsedData?.invoiceGrandTotal ?? null,
        auditId,
        conditionalPrecise: conditionalAmountShowable,
    });
    // `findings` is passed for NAMING only — potentialHeadline reads report_section
    // to avoid calling an adjuster question an "opportunity". It re-grades nothing
    // and cannot change the selected tier or any dollar.
    const headline = potentialHeadline({
        potential,
        findingCount: activeFindings.length,
        verifiedTotal: totalImpact,
        findings: activeFindings,
        fmt,
    });

    return (
        // data-pdf-basis carries the one-line basis statement for the PDF export.
        // It is computed here (the only place the bounded potential exists) and
        // rendered by handleDownloadPDF as a real footer at the foot of the last
        // page — never shown on screen, where the per-finding tier labels already
        // carry the same qualification.
        <div className="impact-summary-panel contractor-hero" data-pdf-basis={potentialBasisLine(potential, fmt)}>
            <div className="contractor-hero-main">
                <div className="contractor-hero-eyebrow">{headline.eyebrow}</div>
                <div className={'contractor-hero-amount' + (headline.isAmount ? '' : ' contractor-hero-amount-msg')}>
                    {headline.amountText}
                </div>
                <div className="contractor-hero-sub">{headline.note}</div>
                {/* REVERSED 2026-09-03. Conditional dollars used to sit INSIDE the
                    headline and this notice said what they still needed. Under
                    HEADLINE_BASIS.VERIFIED_LIKELY they are no longer in the figure,
                    so the old sentence — "$X includes amounts that still need
                    documentation" — is now simply false. It says what is being HELD
                    BACK instead, and names the amount when there is one, so the
                    reader does not have to reconstruct it card by card.

                    What is excluded for other reasons (quarantined rows, single
                    findings above half the estimate, ungraded bases) is still stated
                    separately below, because a customer who cannot see why a number
                    moved cannot defend it. */}
                {conditionalCount > 0 && (
                    <div className="contractor-hero-qa-notice">
                        {potential.conditionalHeld > 0 && conditionalAmountShowable
                            ? `A further ${fmt(potential.conditionalHeld)} across ${conditionalCount} finding${plural(conditionalCount)} is NOT in this figure`
                            : `${conditionalCount} further finding${plural(conditionalCount)} ${conditionalCount === 1 ? 'is' : 'are'} not counted in this figure`}
                        {' '}— {conditionalCount === 1 ? 'it needs' : 'they need'} supporting documentation, vendor
                        pricing, or scope confirmation first. Each one is labeled below with what it needs before
                        you can bill it.
                    </div>
                )}
                {potential.exclusions.length > 0 && (
                    <div className="contractor-hero-qa-notice">
                        {potential.quarantined
                            ? 'This audit predates Audee’s commercial pricing checks, so its stored dollar figures are not shown. Re-run the estimate for a current, gated result.'
                            : `${potential.exclusions.length} finding${plural(potential.exclusions.length)} ${potential.exclusions.length === 1 ? 'was' : 'were'} left out of this total — either too large a share of the estimate to be credible on its own, or lacking a verifiable pricing basis. ${potential.exclusions.length === 1 ? 'It is' : 'They are'} listed below for review.`}
                    </div>
                )}
            </div>

            {nextSteps.length > 0 && (
                <div className="next-steps">
                    <div className="next-steps-title">Next steps</div>
                    <div className="next-steps-list">
                        {nextSteps.map(step => (
                            <div className={`next-step next-step-${step.key}`} key={step.key}>
                                <span className="next-step-icon"><Icon name={step.icon} size={22} /></span>
                                <div className="next-step-body">
                                    <div className="next-step-label">{step.label}</div>
                                    <div className="next-step-desc">{step.desc}</div>
                                </div>
                                {step.amount > 0 && (
                                    <span className={`next-step-amount${step.muted ? ' next-step-amount-muted' : ''}`}>{(step.amountPrefix || '') + fmt(step.amount)}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// Batch 1 step 5: detect an estimate that was NOT read, so a parser miss is never
// presented as a clean pass. Skipped, or zero entries against a printed total above $0
// (GAP-089). Genuinely clean estimates (a true negative with a full set of line items)
// are NOT flagged. Purely presentational — does not touch the parser. Computed from the
// parse (readQuality.js, the mirror of backend/read-quality.js) rather than trusted
// from parsedData.readQuality, so an audit saved under the old $1,000 threshold — or
// reopened from history before readQuality existed — reopens with today's verdict.

// Parsed entry count for display, whichever shape the document is.
function parsedEntryCount(parsedData) {
    const xact = Array.isArray(parsedData?.allLineItems) ? parsedData.allLineItems.length : null;
    if (xact !== null) return xact;
    const tm = countTmScheduleEntries(parsedData);
    return tm !== null ? tm : 0;
}

function isUnderRead(parsedData) {
    return !!parsedData && computeReadQuality(parsedData).underRead;
}

// Compact form of the same warning, shown ABOVE the findings when a document was
// under-read but some findings still survived. The hero form below only renders
// when there is nothing else on the page; without this, a single surviving
// finding caused the under-read state to vanish entirely and the report read as
// a normal successful audit.
function UnderReadNotice({ parsedData }) {
    const li = parsedEntryCount(parsedData);
    const gt = computeReadQuality(parsedData).grandTotal || 0;
    const fmt$ = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    return (
        <div className="ocr-disclosure review" role="status">
            <Icon name="alert-triangle" size={15} />
            {' '}<b>Estimate not read — this is not a complete audit.</b>
            {' '}Audee parsed {li} line item{li === 1 ? '' : 's'}
            {gt > 0 ? <> but the document prints a total of <b>{fmt$(gt)}</b></> : ''}.
            {' '}Anything shown below is based only on what was read, and scope-based
            checks could not run. Treat this as incomplete rather than clean.
        </div>
    );
}

// Shown instead of the Clean Estimate hero when the document was not read. The words
// come from readQuality.notReadCopy, the same sentences the audit-complete email uses.
function UnderReadHero({ parsedData }) {
    const copy = notReadCopy(computeReadQuality(parsedData));
    if (!copy) return null;
    return (
        <div className="clean-estimate-hero under-read-hero">
            <div className="clean-hero-main">
                <div className="clean-hero-badge" style={{ background: 'var(--amber, #f59e0b)', color: '#090a0c' }}>!</div>
                <div className="clean-hero-body">
                    <div className="clean-hero-title">{copy.title}</div>
                    <div className="clean-hero-message">{copy.lead}</div>
                    <div className="clean-hero-subtitle">
                        {copy.totalLine && <>{copy.totalLine} </>}{copy.closing}
                    </div>
                </div>
            </div>
        </div>
    );
}

// Shown in place of the standard impact summary when an estimate has zero
// findings — a success hero at the top of the report so the user sees the audit
// passed without scrolling. Purely presentational.
function CleanEstimateHero({ parsedData }) {
    const lineItems = Array.isArray(parsedData?.allLineItems) ? parsedData.allLineItems.length : null;
    // Trust stats: only surface counts we genuinely have. There is no reliable
    // "audit rules evaluated" count available to the report today, so it is omitted
    // rather than invented.
    const stats = [];
    if (lineItems !== null) stats.push({ label: 'Line items reviewed', value: lineItems });

    return (
        <div className="clean-estimate-hero">
            <div className="clean-hero-main">
                <div className="clean-hero-badge"><Icon name="check" size={30} strokeWidth={2.5} /></div>
                <div className="clean-hero-body">
                    <div className="clean-hero-title">Clean Estimate</div>
                    <div className="clean-hero-message">Audit Complete — No Actionable Findings</div>
                    <div className="clean-hero-subtitle">
                        This estimate appears complete based on Audee's current audit rules.
                    </div>
                </div>
                {/* No "$0.00" tile. Under potential-first a dollar figure is never
                    the lead, and a zero one least of all — this state means the rules
                    found nothing to flag, which is a count fact, not a money fact.
                    (It has not occurred in 164 production audits; the engine
                    concludes documents are unpriceable, not clean.) */}
                <div className="clean-hero-stat">
                    <div className="clean-hero-stat-amount">0</div>
                    <div className="clean-hero-stat-label">Findings</div>
                </div>
            </div>
            {stats.length > 0 && (
                <div className="clean-hero-stats">
                    {stats.map(s => (
                        <div className="clean-hero-stat-chip" key={s.label}>
                            <span className="clean-hero-stat-chip-value">{s.value}</span>
                            <span className="clean-hero-stat-chip-label">{s.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// At-a-glance estimate metadata, read straight from already-parsed data.
// Purely presentational: no parsing, no math, no finding mutation.
// ── Line-item notes checklist ──────────────────────────────────────────────
// Which line items carry no F9 note, so the estimator can add them in Xactimate
// before submitting. ONE section per document, never a finding: it reads parsed
// data only and touches no finding, tier, hero or total (see lineItemNotes.js and
// verify-line-item-notes.js). Collapsed by default; every missing line is listed
// when opened. Not rendered on under-read documents, on parses from before
// notesVersion 2, or where the parse carries no notes field.
function LineItemNotesChecklist({ parsedData }) {
    const [open, setOpen] = useState(false);
    // The report's own under-read verdict (readQuality.underRead plus its fallback),
    // so the checklist never speaks on a document the report already calls under-read.
    const checklist = isUnderRead(parsedData) ? null : buildNotesChecklist(parsedData);
    if (!checklist) return null;

    if (checklist.state === 'all-noted') {
        return (
            <div className="report-section notes-checklist">
                <div className="notes-checklist-quiet">Every line item has an F9 note.</div>
            </div>
        );
    }
    if (checklist.state === 'none-found') {
        return (
            <div className="report-section notes-checklist">
                <div className="notes-checklist-quiet">
                    We didn't find F9 notes on this estimate. If it has them, re-export with notes included.
                </div>
            </div>
        );
    }

    const { groups, missingCount, total } = checklist;
    return (
        <div className="report-section notes-checklist">
            <button
                type="button"
                className="section-header notes"
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
            >
                <span className="section-icon"><Icon name="clipboard" size={20} /></span>
                Line-item notes
                <span className="section-count">{missingCount}</span>
                <span className="section-chevron"><Icon name={open ? 'chevron-up' : 'chevron-down'} size={14} /></span>
            </button>
            <div className="notes-checklist-summary">
                {missingCount} of {total} line items have no F9 note.
            </div>
            {open && (
                <div className="notes-checklist-groups">
                    {groups.map(g => (
                        <div className="notes-checklist-group" key={g.key}>
                            <div className="notes-checklist-room">{groupLabel(g, groups)}</div>
                            <ul className="notes-checklist-rows">
                                {g.rows.map(r => (
                                    <li className="notes-checklist-row" key={r.lineNumber}>
                                        <span className="notes-checklist-line">{r.lineNumber}.</span>
                                        {r.code && <span className="notes-checklist-code">{r.code}</span>}
                                        <span className="notes-checklist-desc">{r.description}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// Property type line for Xactimate reports (GAP-097). Says whether the uploader picked
// it or the classifier detected it. Older reports with no occupancy on the stored parse
// render nothing rather than guess.
const PICKED_PROPERTY_TYPE = { residential: 'Residential', commercial: 'Commercial', multi_unit: 'Multi-unit' };
const DETECTED_PROPERTY_TYPE = { residential: 'Residential', commercial: 'Commercial', mixed: 'Mixed use' };
function propertyTypeText(parsedData) {
    const ctx = parsedData.estimateContext || {};
    if (ctx.occupancySource === 'user' && PICKED_PROPERTY_TYPE[ctx.userOccupancy]) {
        return `${PICKED_PROPERTY_TYPE[ctx.userOccupancy]} (you selected)`;
    }
    if (DETECTED_PROPERTY_TYPE[ctx.occupancyType]) return `${DETECTED_PROPERTY_TYPE[ctx.occupancyType]} (detected from the estimate)`;
    if (ctx.occupancyType === 'unknown') return 'Not determined';
    return null;
}

// "Is this property a home or a business?" (GAP-097). Shown only when the backend
// stamped askOccupancy: the classifier could not tell (unknown / mixed) and one of the
// answers changes a finding on this estimate. A confident verdict or an earlier answer
// never asks. The answer re-runs the rules on the saved parse; kept out of the PDF.
function OccupancyQuestion({ parsedData, onSetOccupancy }) {
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const ctx = parsedData.estimateContext || {};
    if (!onSetOccupancy || ctx.askOccupancy !== true || ctx.occupancySource !== 'classifier') return null;
    const answer = async (occupancy) => {
        setBusy(occupancy);
        setError(null);
        try { await onSetOccupancy(occupancy); } catch (e) { setError(e.message); setBusy(null); }
    };
    return (
        <div className="occupancy-question" data-html2canvas-ignore="true">
            <span className="occupancy-question-text">Is this property a home or a business?</span>
            <button type="button" className="action-btn secondary" disabled={!!busy} onClick={() => answer('residential')}>
                {busy === 'residential' ? 'Updating…' : 'Home'}
            </button>
            <button type="button" className="action-btn secondary" disabled={!!busy} onClick={() => answer('commercial')}>
                {busy === 'commercial' ? 'Updating…' : 'Business'}
            </button>
            {error && <span className="occupancy-question-error">{error}</span>}
        </div>
    );
}

function EstimateOverview({ parsedData }) {
    const carrier = parsedData.carrier && String(parsedData.carrier).trim()
        ? String(parsedData.carrier).trim()
        : 'Unknown Carrier';
    const priceList = parsedData.priceListCode || '—';
    const lossType = parsedData.lossType || parsedData.coverPage?.typeOfLoss || '—';

    const rawTotal = parsedData.invoiceGrandTotal ?? parsedData.grandTotal ?? null;
    const totalNum = rawTotal === null || rawTotal === undefined
        ? null
        : parseFloat(String(rawTotal).replace(/[$,]/g, ''));
    const estimateTotal = totalNum !== null && !Number.isNaN(totalNum)
        ? `$${totalNum.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
        : '—';

    const rooms = typeof parsedData.totalRooms === 'number' ? parsedData.totalRooms : null;
    // "not read", never a confident 0 or —, on a document whose rows Audee could not read.
    const lineItems = isUnderRead(parsedData) ? 'not read'
        : Array.isArray(parsedData.allLineItems) ? parsedData.allLineItems.length : null;

    const cp = parsedData.coverPage || {};
    const date = cp.dateOfLoss || cp.dateEstCompleted || cp.dateEntered || cp.dateJobCompleted || '—';

    // Roofing estimates are not organized by room, so the "Rooms" stat is omitted
    // rather than shown as an empty/irrelevant count. Discipline is set by the
    // estimate classification (parsedData.estimateContext.discipline).
    const isRoofing = parsedData.estimateContext?.discipline === 'roofing';

    const fields = [
        { label: 'Carrier', value: carrier, mono: false, unknown: carrier === 'Unknown Carrier' },
        { label: 'Price list', value: priceList, mono: true },
        { label: 'Loss type', value: lossType, mono: false },
        { label: 'Estimate total', value: estimateTotal, mono: true, accent: true },
        ...(isRoofing ? [] : [{ label: 'Rooms', value: rooms === null ? '—' : rooms, mono: true }]),
        { label: 'Line items', value: lineItems === null ? '—' : lineItems, mono: true },
        { label: 'Date', value: date, mono: true },
    ];

    return (
        <div className="estimate-overview-card">
            <div className="estimate-overview-header">Estimate overview</div>
            <div className="estimate-overview-grid">
                {fields.map(f => (
                    <div className="estimate-overview-field" key={f.label}>
                        <div className="estimate-overview-label">{f.label}</div>
                        <div className={
                            'estimate-overview-value'
                            + (f.mono ? ' mono' : '')
                            + (f.accent ? ' accent' : '')
                            + (f.unknown ? ' unknown' : '')
                        }>
                            {f.value}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Top 5 findings by dollar_impact, with a jump-to-finding action. Reads the
// same findings the report already renders; ranking only, no math changes.
function TopOpportunities({ findings, dismissedIds, onJump }) {
    const active = findings.filter(f => !dismissedIds.has(dismissKey(f)));
    // Overlap grouping computed over exactly the set we rank, so a co-firing gap is
    // represented once, by its primary. Contributing members are folded under the
    // primary's card below and must not appear here as separate ranked lines (that
    // is the padding we remove).
    const overlap = assignOverlapRoles(active);
    const ranked = active
        .filter(f => !overlap.contributingSet.has(f.rule_id))
        .filter(f => (f.dollar_impact || 0) > 0)
        .sort((a, b) => (b.dollar_impact || 0) - (a.dollar_impact || 0))
        .slice(0, 5);

    if (ranked.length === 0) return null;

    return (
        <div className="top-opps-panel">
            <div className="top-opps-header">
                <span className="top-opps-title">Top {ranked.length} opportunities</span>
                <span className="top-opps-subtitle">Ranked by dollar impact</span>
            </div>
            <ol className="top-opps-list">
                {ranked.map((f, i) => {
                    const sev = (f.severity || '').toLowerCase();
                    return (
                        <li className="top-opp-row" key={cardKey(f)}>
                            <span className="top-opp-rank">{i + 1}</span>
                            <div className="top-opp-main">
                                <span className="top-opp-name">{f.customerLabel || f.rule_name}</span>
                                <span className="top-opp-meta">
                                    <span className="top-opp-ruleid">{f.rule_id}</span>
                                    <span className={`severity-badge severity-${sev}`}>{f.severity}</span>
                                    {typicalRateNote(f) && <span className="top-opp-basis" title={typicalRateNote(f)}>Typical rate</span>}
                                </span>
                            </div>
                            <span className="top-opp-amount">
                                ${ (f.dollar_impact || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }) }
                            </span>
                            <button className="top-opp-jump" onClick={() => onJump(f.rule_id)}>
                                Jump <Icon name="arrow-right" size={13} />
                            </button>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}

// UX Cleanup Sprint — the Opportunities report section. Same findings, same
// dollar impacts, same evidence: this only controls how many are visible by
// default to cut clutter. Nothing is removed — below-threshold and beyond-top-5
// findings live behind disclosure toggles. During PDF export (generating) every
// opportunity renders fully so the exported report stays complete.
const OPP_TOP_N = 5;          // Kept for the "show fewer" affordance only.

// ── NO DOLLAR THRESHOLD ON ADDITIONS (founder decision, 2026-08-28) ──────────
//
// There was a $500 line: additions above it listed, additions below it swept into
// a separate "small opportunities" group that collapsed itself once there were
// more than five. Retired. Founder: "small opportunities [are not] an afterthought,
// collapsed menu. That stuff can really add up if you've got 10-15 $300
// opportunities."
//
// The corpus agrees. Across 68 recent Xactimate documents Opportunity carries 878
// of 984 customer-visible findings and averages $17,013 per document — and 66% of
// those documents have an EMPTY Requires Correction tier, so for two operators in
// three the collapsed section was the entire report. A $300 item is not noise on a
// $10,000 mitigation job; ten of them are the reason to run the audit.
//
// Every addition now renders in one list, sorted by dollar, nothing hidden behind
// a threshold. OPP_MIN_IMPACT and SMALL_OPP_EXPAND_MAX are deliberately gone
// rather than set to 0 — a threshold constant left lying around gets re-used.

// The related/contributing block shown UNDER a primary finding's card. Lists the
// other rules that flagged the same underlying gap — by neutral customer label,
// with NO dollar of their own (their amounts stay in their own tiers and totals;
// they are simply not repeated here as separate line items). In the reviewer/Pro
// view every contributing rule_id is exposed so nothing is hidden from review.
function RelatedContributors({ contributors }) {
    const density = useContext(DensityContext);
    if (!contributors || contributors.length === 0) return null;
    return (
        <div className="finding-related">
            <div className="finding-related-label">
                Also flagged for this item — counted once above, not billed separately
            </div>
            <ul className="finding-related-list">
                {contributors.map(cf => (
                    <li className="finding-related-item" key={cf.rule_id}>
                        <span className="finding-related-name">{cf.customerLabel || cf.rule_name}</span>
                        {density === 'pro' && (
                            <span className="finding-related-ruleid">{cf.rule_id}</span>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

// Empty-tier copy (2026-09-03).
//
// Every tier used to disappear when it had no findings. Measured on the
// deduplicated corpus (201 unique documents), that is not an edge case: on
// Xactimate documents the Requires Correction tier is empty 107 times out of 158,
// and on T&M documents the Opportunity tier is empty 32 times out of 43. A SERVPRO
// beta user finished a T&M report, saw nothing under additions,
// and had to email to ask whether that meant she had missed nothing. Operators
// running unattended trials will not send that email — they will just draw their
// own conclusion, and the flattering conclusion is the wrong one.
//
// Two distinct empty states, because they mean different things:
//   · filtered  — the tier has findings, the operator's severity filter hid them.
//                 Say so. Never imply a clean result.
//   · genuinely empty — nothing fired. Say what that does and does not prove.
//
// The Opportunity copy deliberately stops short of claiming completeness. Absence
// of an addition means nothing was found that the file supports, not that nothing
// was missed on site — and on T&M the rule coverage is thinner than on Xactimate,
// so that caveat is load-bearing rather than decorative.
function EmptyTierNote({ filtered, children }) {
    return (
        <div className="section-empty" role="status">
            {filtered
                ? <>No findings at the selected severity. Clear the filter to see everything in this section.</>
                : children}
        </div>
    );
}

// MATERIALITY (2026-09-04). A threshold below which an addition is real but not
// worth its own card on THIS job.
//
// This is not the $500 small/large split retired on 2026-08-28. That split was
// FIXED, and the founder's objection to it was correct: "$300 is not noise on a
// $10,000 mitigation," and ten of them add up. The threshold here SCALES with the
// estimate, so on a $10k job it is $5 and nothing folds. It only starts to bite
// where a fixed number never should have applied in the first place.
//
// What forced it: SBA Building 333, $1.31M, commercial, 74 rooms, parsed cleanly
// — 32 addition cards including a $22 wax ring, $28 supply lines, $28 sill pan,
// $28 container lock, $18 foam insulation. Every one correct; none worth a card
// on a job that size. Bryan Newell (Signal) described that exact experience,
// unprompted, as the reason estimating tools read as "residential."
//
// Rules: 0.05% of the estimate, capped at $500 ($1M+ jobs), so the St. Louis job at
// $155k folds under $77 and a $30k water job folds under $15. Folding needs at
// least three items or it is just a shorter list with a label. Unpriced
// findings (dollar withheld) are never folded: calling something "minor" when
// its size is unknown would be a claim. The fold changes NOTHING about totals,
// counts, or which findings exist — it is a card-list presentation, and the PDF
// export expands it.
const MATERIALITY = Object.freeze({
    SHARE_OF_ESTIMATE: 0.0005,
    CAP: 500,
    MIN_ITEMS_TO_FOLD: 3,
});

function materialityThreshold(grandTotal) {
    const gt = parseFloat(String(grandTotal ?? '').replace(/[$,\s]/g, ''));
    if (!Number.isFinite(gt) || gt <= 0) return 0;
    return Math.min(MATERIALITY.CAP, gt * MATERIALITY.SHARE_OF_ESTIMATE);
}

function isMinorAddition(f, threshold) {
    if (threshold <= 0) return false;
    const amt = Number(f && f.dollar_impact);
    // Unknown or withheld amount → not minor, by policy.
    if (!Number.isFinite(amt) || amt <= 0) return false;
    return amt < threshold;
}

// Trade codes → words, for the lump-sum notice only. Anything unmapped shows as
// its code, which an estimator reads fine.
const TRADE_LABEL = {
    HVC: 'HVAC', INS: 'insulation', RFG: 'roofing', ELE: 'electrical', PLM: 'plumbing',
    EQU: 'equipment', LAB: 'labor', FPS: 'fire protection', DMO: 'demolition', FRM: 'framing',
    SDG: 'siding', WDW: 'windows', CNC: 'concrete', ELS: 'solar', CON: 'contents', FEE: 'professional fees',
};
const tradeLabel = (code) => TRADE_LABEL[code] || code;

// Lump-sum notice (2026-09-07). When an estimate carries whole trades as bid
// items, custom "*" items or one-each lump lines, the missing-item checks for
// those trades are muted server-side (lump-sum-context.js). This says so, in
// the section where those checks would have appeared, so a large-loss
// estimator sees the tool understood the document rather than assuming it
// didn't. Renders nothing when there are no lumps.
function LumpSumNotice({ ctx, fmt$ }) {
    if (!ctx || !ctx.count) return null;
    const muted = Array.isArray(ctx.mutedFindings) ? ctx.mutedFindings.length : 0;
    const trades = (ctx.trades || []).map(tradeLabel);
    const pct = ctx.share != null ? `${Math.round(ctx.share * 100)}% of this estimate` : null;
    return (
        <div className="lump-sum-notice" role="note">
            <b>{ctx.count} line{ctx.count === 1 ? '' : 's'} priced as {ctx.count === 1 ? 'a lump sum or bid item' : 'lump sums or bid items'}</b>
            {' '}— {fmt$(ctx.dollars)}{pct ? `, ${pct}` : ''}. Audee can't audit inside a lump.
            {trades.length > 0 && (
                <> Missing-item checks for {trades.join(', ')} are muted on this report
                {muted > 0 ? ` (${muted} ${muted === 1 ? 'finding' : 'findings'} held back)` : ''}.</>
            )}
        </div>
    );
}

function OpportunitiesSection({ opportunities, onDismiss, onRestore, generating, totalBeforeFilter, isTmDocument, grandTotal, lumpSum, isCotality }) {
    // Overlap grouping (display only): a co-firing gap renders once, as its primary
    // card, with the other members nested underneath as related/contributing rather
    // than as separate dollar line items. So the standalone card list drops the
    // contributing findings, and each primary carries its contributors with it.
    // Computed over exactly the (already severity-filtered) list this section shows,
    // so a group whose primary is filtered out simply re-groups among what remains —
    // a contributor is never orphaned by the filter.
    const overlap = assignOverlapRoles(opportunities);
    const byRuleId = new Map(opportunities.map(f => [f.rule_id, f]));
    const contributorsFor = (f) => (overlap.contributorsOf.get(f.rule_id) || [])
        .map(rid => byRuleId.get(rid))
        .filter(Boolean);

    // ONE LIST, sorted by dollar. No small/large split — see the note on
    // OPP_TOP_N above. The caller already sorts by dollar_impact descending.
    const visible = opportunities.filter(f => !overlap.contributingSet.has(f.rule_id));

    // OPEN BY DEFAULT (founder decision, 2026-08-28), superseding the collapse of
    // 2026-08-25. Additions are the money the contractor can add, and the founder's
    // read is that this is what an operator wants the instant the audit finishes.
    // Nothing is hidden on arrival; the operator can collapse it if they want to.
    const [oppOpen, setOppOpen] = useState(true);

    // Every addition is shown. This exists only so a very long list can be trimmed
    // back BY THE OPERATOR — it starts fully expanded and never self-collapses.
    const [showAll, setShowAll] = useState(true);

    // The materiality fold starts closed: that is its purpose. One click opens it.
    const [minorOpen, setMinorOpen] = useState(false);

    // Cotality findings carry no dollar (GAP-078), so the section does not wear one.
    const sectionIcon = isCotality ? <Icon name="clipboard" size={20} /> : '$';
    // Cotality counts items and the groups (cards) they sit in; elsewhere, findings.
    const cardCount = isCotality ? cotalityCountText(visible) : visible.length;

    // Previously: `if (opportunities.length === 0) return null;` — the section
    // vanished silently. It now renders its header and says what empty means.
    if (opportunities.length === 0) {
        return (
            <div className="report-section">
                <div className="section-header opportunity">
                    <span className="section-icon">{sectionIcon}</span>
                    Potential additions
                    <span className="section-count">{isCotality ? itemsText(0) : 0}</span>
                </div>
                <EmptyTierNote filtered={totalBeforeFilter > 0}>
                    <b>No additional scope identified.</b>{' '}
                    Nothing supported by this document appears to be missing. That reflects
                    what Audee could read in the file — it is not a guarantee that nothing
                    was missed on site.
                    {isTmDocument && (
                        <> Addition checks are strongest on Xactimate reconstruction scope;
                        coverage on T&amp;M documents is still expanding, so an empty result
                        here carries less weight than it would on an Xactimate estimate.</>
                    )}
                </EmptyTierNote>
            </div>
        );
    }

    // PDF export must include everything regardless of toggle state.
    const expandAll = showAll || generating;
    const sectionOpen = oppOpen || generating;

    // Materiality split. `visible` is already sorted by dollar descending, so
    // the minor items are simply its tail — order within each group is preserved.
    const threshold = materialityThreshold(grandTotal);
    const minorCandidates = visible.filter(f => isMinorAddition(f, threshold));
    const folding = minorCandidates.length >= MATERIALITY.MIN_ITEMS_TO_FOLD;
    const major = folding ? visible.filter(f => !isMinorAddition(f, threshold)) : visible;
    const minor = folding ? minorCandidates : [];
    const minorTotal = minor.reduce((s, f) => s + (Number(f.dollar_impact) || 0), 0);

    const shown = expandAll ? major : major.slice(0, OPP_TOP_N);
    const remaining = major.length - shown.length;

    const renderCard = (f) => (
        <React.Fragment key={cardKey(f)}>
            <FindingCard finding={f} onDismiss={onDismiss} onRestore={onRestore} dismissed={false} generating={generating} />
            <RelatedContributors contributors={contributorsFor(f)} />
        </React.Fragment>
    );
    const renderCards = (list) => groupCotality(list).map(g => (g.length > 1
        ? <CotalityGroupCard key={cardKey(g[0])} findings={g} onDismiss={onDismiss} onRestore={onRestore} dismissed={false} generating={generating} />
        : renderCard(g[0])));

    const fmt$ = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <div className="report-section">
            <div
                className="section-header opportunity"
                onClick={() => !generating && setOppOpen(o => !o)}
                style={{ cursor: generating ? 'default' : 'pointer' }}
                role={generating ? undefined : 'button'}
                aria-expanded={generating ? undefined : sectionOpen}
            >
                <span className="section-icon">{sectionIcon}</span>
                Potential additions
                <span className="section-count">{cardCount}</span>
                {!generating && (
                    <span className="section-toggle-hint" style={{ marginLeft: 'auto', fontWeight: 400, fontSize: 13, opacity: 0.7 }}>
                        {sectionOpen ? 'Hide' : `Show ${cardCount}`}
                    </span>
                )}
            </div>
            {!sectionOpen ? null : (<>
            <LumpSumNotice ctx={lumpSum} fmt$={fmt$} />
            <div className="findings-list">
                {renderCards(shown)}
            </div>
            {!generating && remaining > 0 && (
                <button className="opp-disclosure" onClick={() => setShowAll(true)}>
                    Show remaining {remaining} {remaining === 1 ? 'addition' : 'additions'}
                </button>
            )}
            {!generating && showAll && major.length > OPP_TOP_N && (
                <button className="opp-disclosure subtle" onClick={() => setShowAll(false)}>
                    Show top {OPP_TOP_N} only
                </button>
            )}
            {minor.length > 0 && (
                <div className="minor-additions">
                    <button
                        className="minor-additions-toggle"
                        onClick={() => !generating && setMinorOpen(o => !o)}
                        aria-expanded={minorOpen || generating}
                        disabled={generating}
                    >
                        <span className="minor-additions-count">{minor.length}</span>
                        {' '}minor {minor.length === 1 ? 'addition' : 'additions'} under {fmt$(threshold)} each
                        {' '}· <b>{fmt$(minorTotal)}</b> combined
                        <span className="minor-additions-hint">
                            {(minorOpen || generating) ? 'Hide' : 'Show'}
                        </span>
                    </button>
                    <div className="minor-additions-note">
                        Still counted in the totals above — folded here because each is small relative to a {fmt$(grandTotal)} estimate.
                    </div>
                    {(minorOpen || generating) && (
                        <div className="findings-list">
                            {renderCards(minor)}
                        </div>
                    )}
                </div>
            )}
            </>)}
        </div>
    );
}

// Objective 4: the audit id lives in ?audit=<id> (the app's router-free refresh-
// persistence pattern — see urlState.js). Reading it here means outcome capture works
// on both a fresh save and a re-opened report without threading a new prop from App.
const currentAuditId = () => {
    if (typeof window === 'undefined') return null;
    const v = new URLSearchParams(window.location.search).get('audit');
    return v && /^\d+$/.test(v) ? v : null;
};

function AuditReport({ results, onReset, fileName, session, onSetOccupancy }) {
    const { parsedData, findings } = results;

    // ── Objective 4: finding outcome capture state ──────────────────────────
    // Keyed by rule_id, mirroring the (audit_id, rule_id) identity of an outcome.
    const [auditId, setAuditId] = useState(currentAuditId);
    const [outcomes, setOutcomes] = useState({});
    const [outcomeBusy, setOutcomeBusy] = useState({});

    // The id appears in the URL only once App has saved the audit, which happens after
    // this component first renders. Re-check on results change so capture appears for a
    // fresh run without a reload.
    useEffect(() => { setAuditId(currentAuditId()); }, [results]);

    // Load previously-recorded outcomes so a re-opened report shows what the contractor
    // already told us rather than an empty form. Best-effort: a failure just means no
    // prefill, never a broken report.
    useEffect(() => {
        let cancelled = false;
        if (!auditId) return;
        (async () => {
            try {
                const res = await fetch(
                    `https://tm-audit-platform-production.up.railway.app/api/finding-outcome/${auditId}`,
                    { headers: await getAuthHeaders() }
                );
                if (!res.ok) return;
                const json = await res.json();
                if (cancelled) return;
                const byRule = {};
                for (const o of (json.outcomes || [])) byRule[o.rule_id] = o;
                setOutcomes(byRule);
            } catch { /* no prefill */ }
        })();
        return () => { cancelled = true; };
    }, [auditId]);

    const saveOutcome = useCallback(async (ruleId, payload) => {
        if (!auditId) return { ok: false, error: 'This audit has not been saved yet.' };
        setOutcomeBusy(b => ({ ...b, [ruleId]: true }));
        try {
            const res = await fetch(
                'https://tm-audit-platform-production.up.railway.app/api/finding-outcome',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
                    body: JSON.stringify({ audit_id: Number(auditId), rule_id: ruleId, ...payload }),
                }
            );
            const json = await res.json().catch(() => ({}));
            if (!res.ok) return { ok: false, error: json.error || 'Could not save outcome.' };
            setOutcomes(o => ({ ...o, [ruleId]: json.outcome }));
            return { ok: true };
        } catch (e) {
            return { ok: false, error: 'Network error — try again.' };
        } finally {
            setOutcomeBusy(b => ({ ...b, [ruleId]: false }));
        }
    }, [auditId]);
    const rawTotal = parsedData.invoiceGrandTotal || parsedData.grandTotal || null;
    const displayTotal = rawTotal !== null && rawTotal !== undefined
        ? (typeof rawTotal === 'number' ? rawTotal.toFixed(2) : rawTotal)
        : null;
    // documentType derived via parsedData.documentType directly in JSX

    // Derive job name from filename first, fall back to parsed name
    const jobName = fileName
        ? fileName.replace(/\.[^.]+$/, '').replace(/\s*Summary of Charges.*$/i, '').replace(/\s*as of.*$/i, '').trim()
        : parsedData.jobName || 'Audit Report';

    // Export counter - reserved for future use
    // const getExportCount = () => {
    //     const key = 'exportCount_' + jobName.replace(/\s+/g, '').toLowerCase();
    //     const count = parseInt(localStorage.getItem(key) || '0') + 1;
    //     localStorage.setItem(key, count.toString());
    //     return count;
    // };

    // const safeJobName = jobName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const [dismissedIds, setDismissedIds] = useState(new Set());
    const [reviewedOpen, setReviewedOpen] = useState(true);
    const [suppressedOpen, setSuppressedOpen] = useState(false);
    // Contractor Mode: the detailed audit (filters, finding sections)
    // is expanded by default so the flow reads Summary → Details → Feedback. The
    // user can collapse it via "Hide Audit Details". PDF export always renders
    // details so the export stays complete.
    const [detailsOpen, setDetailsOpen] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [severityFilter, setSeverityFilter] = useState('All');
    const [viewMode, setViewMode] = useState('severity'); // 'severity' | 'room'

    // Report density — "simple" (owner default) or "pro" (technical inline). Folds
    // the old All/Contractor/Adjuster audience control into one clear switch and
    // persists across reports. Presentation/view-state only. localStorage may be
    // unavailable (private mode / SSR) — fall back to the default silently.
    const [density, setDensity] = useState(() => {
        try {
            const v = window.localStorage.getItem('audee.report.density');
            return v === 'pro' ? 'pro' : 'simple';
        } catch { return 'simple'; }
    });
    useEffect(() => {
        try { window.localStorage.setItem('audee.report.density', density); } catch { /* ignore */ }
        if (density === 'pro') interactionRef.current.sections.add('pro_view');
    }, [density]);

    // Feedback auto-capture: lightweight interaction signals collected for the
    // FeedbackWidget. Observation only — refs, so tracking never re-renders the
    // report or touches any audit/report logic.
    const reportMountRef = useRef(Date.now());
    const interactionRef = useRef({ pdfDownloaded: false, detailsExpanded: false, sections: new Set() });

    useEffect(() => {
        if (detailsOpen) {
            interactionRef.current.detailsExpanded = true;
            interactionRef.current.sections.add('audit_details');
        }
    }, [detailsOpen]);
    useEffect(() => { if (suppressedOpen) interactionRef.current.sections.add('suppressed'); }, [suppressedOpen]);
    useEffect(() => { if (viewMode === 'room') interactionRef.current.sections.add('room_view'); }, [viewMode]);

    // Resolved at submit time so values (elapsed time, pdf flag, expansions) are
    // current. Everything is best-effort; missing values are simply null.
    const getFeedbackRuntime = () => {
        const i = interactionRef.current;
        return {
            related_release: RELEASE_ID,
            related_commit: COMMIT_ID,
            browser_width: typeof window !== 'undefined' ? window.innerWidth : null,
            browser_height: typeof window !== 'undefined' ? window.innerHeight : null,
            time_on_report_seconds: Math.max(0, Math.round((Date.now() - reportMountRef.current) / 1000)),
            pdf_downloaded: i.pdfDownloaded,
            details_expanded: i.detailsExpanded,
            sections_expanded: [...i.sections],
            current_url: typeof window !== 'undefined' ? window.location.href : null,
            browser_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        };
    };

    const { targetRef } = usePDF({
        filename: 'tm-audit-report.pdf',
        page: { margin: 20, format: 'letter', orientation: 'portrait' },
        overrides: {
            canvas: { useCORS: true },
            pdf: { compress: true },
        },
    });

    const handleDismiss = (ruleId) => {
        setDismissedIds(prev => new Set([...prev, ruleId]));
    };

    const handleRestore = (ruleId) => {
        setDismissedIds(prev => {
            const next = new Set(prev);
            next.delete(ruleId);
            return next;
        });
    };

    // Jump to a finding card from the Top Opportunities panel. Reset the view
    // to severity + All severities first so the target is guaranteed rendered,
    // then scroll to its anchor (added on FindingCard's root: finding-<ruleId>).
    const handleJumpToFinding = (ruleId) => {
        setViewMode('severity');
        setSeverityFilter('All');
        setTimeout(() => {
            const el = document.getElementById(`finding-${ruleId}`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('finding-flash');
                setTimeout(() => el.classList.remove('finding-flash'), 1600);
            }
        }, 60);
    };

    const activeFindings = findings.filter(f => !dismissedIds.has(dismissKey(f)));
    const dismissedFindings = findings.filter(f => dismissedIds.has(dismissKey(f)));

    // Overlap grouping (display only) is applied inside TopOpportunities and
    // OpportunitiesSection, each over exactly the finding list it renders: when
    // several rules flag the SAME underlying gap (e.g. floor protection: OPP008 +
    // X002 + PNT002), the customer list shows ONE primary finding with the others
    // nested as related, instead of repeating the gap as separate dollar lines. It
    // never mutates findings, tiers, or totals — the hero/value_tiers sums still
    // include every finding. See overlapGroups.js.

    // Roofing estimates are not organized by room, so the report presents no
    // room-organization UI for them. The discipline is decided automatically by
    // the estimate classification (parsedData.estimateContext.discipline) — the
    // user never chooses. The backend already withholds per-finding room_context
    // and room_recommendations on a roofing discipline, so the finding-level room
    // badges and recommendations disappear on their own; isRoofing additionally
    // suppresses the By-Room toggle/view. Every other discipline keeps the
    // existing room functionality unchanged.
    const isRoofing = parsedData.estimateContext?.discipline === 'roofing';

    // Audience filtering (All/Contractor/Adjuster) was folded into the Simple/Pro
    // density switch: every finding is shown, and Pro reveals the adjuster-facing
    // technical detail inline rather than filtering findings out. So all active
    // findings flow through (kept as `audienceFiltered` to avoid churn downstream).
    const audienceFiltered = activeFindings;

    const filteredFindings = severityFilter === 'All'
        ? audienceFiltered
        : audienceFiltered.filter(f => f.severity === severityFilter);

    const hasRoomContext = !isRoofing && audienceFiltered.some(f => f.room_context?.label);

    // When the By-Room toggle is not available (roofing, or no room data at all),
    // the room view is unreachable — guarantee viewMode falls back to severity so
    // the severity-guarded sections always render.
    useEffect(() => {
        if (!hasRoomContext && viewMode !== 'severity') setViewMode('severity');
    }, [hasRoomContext, viewMode]);

    // Suppressed findings (scope/occupancy set-aside, dollars zeroed by
    // calibration) are pulled out of the active sections and collected into a
    // de-emphasized "Suppressed / Reviewer Notes" section so they never dominate
    // the first pages. Data and reviewer visibility are unchanged — this is
    // purely where they render. Independent of the severity filter, like REVIEWED.
    const isSuppressed = (f) => !!f.suppressed;

    // Findings the customer actually sees. Used to decide the hero: a report whose
    // findings are ALL suppressed has nothing to show and must fall through to the
    // under-read / clean hero rather than render an empty summary.
    // NB: deliberately NOT named activeFindings — that identifier is already taken
    // in this scope (line ~1476) for the dismissed-filter, which is a different idea.
    const customerVisibleFindings = findings.filter(f => !isSuppressed(f));

    // A document Audee did not read, with nothing customer-visible, offers no findings
    // section at all: its suppressed findings are absence checks that could not run, and
    // "Show audit details" under a not-read hero reads as if an audit happened.
    const hasDetails = findings.length > 0 && !(isUnderRead(parsedData) && customerVisibleFindings.length === 0);

    const requiresCorrection = filteredFindings
        .filter(f => f.report_section === 'Requires Correction' && !isSuppressed(f))
        .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

    const prepareToExplain = filteredFindings
        .filter(f => f.report_section === 'Prepare to Explain' && !isSuppressed(f))
        .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

    const suppressedFindings = audienceFiltered.filter(isSuppressed);

    // Count for a tier BEFORE the severity filter is applied, so an empty section
    // can tell the operator which kind of empty it is. audienceFiltered is the
    // dismissed-filtered set, i.e. everything the operator has not set aside; if
    // that is non-zero while the rendered tier is zero, the severity filter is the
    // only thing hiding them and we must not imply the estimate is clean.
    const unfilteredCount = (section) =>
        audienceFiltered.filter(f => f.report_section === section && !isSuppressed(f)).length;

    // T&M / SOC documents carry their entries in the Servpro schedule arrays and
    // never populate allLineItems. Opportunity rule coverage is materially thinner
    // on that path, so the empty-additions copy says so rather than letting an
    // empty section read as a clean bill.
    const isTmDocument = !!parsedData
        && !Array.isArray(parsedData.allLineItems)
        && ['SOC', 'Invoice'].includes(parsedData.documentType);
    const isCotality = isCotalityReport(parsedData);
    const parseWarning = isCotality && cotalityParseWarning(parsedData);

    // Detail sections render when the contractor opens them, and always during
    // PDF export so the exported report is complete.
    const showDetails = detailsOpen || generating;

    const applyLightTheme = () => {
        const root = document.documentElement;
        root.style.setProperty('--bg', '#ffffff');
        root.style.setProperty('--surface', '#f8f9fa');
        root.style.setProperty('--surface2', '#f1f3f5');
        root.style.setProperty('--border', '#dee2e6');
        root.style.setProperty('--text', '#1a1a1a');
        root.style.setProperty('--text-muted', '#6b7280');
        document.body.style.background = '#ffffff';
    };

    const restoreDarkTheme = () => {
        const root = document.documentElement;
        root.style.removeProperty('--bg');
        root.style.removeProperty('--surface');
        root.style.removeProperty('--surface2');
        root.style.removeProperty('--border');
        root.style.removeProperty('--text');
        root.style.removeProperty('--text-muted');
        document.body.style.background = '';
    };

    const handleDownloadPDF = async () => {
        // Record the download for feedback context. Does not alter PDF output.
        interactionRef.current.pdfDownloaded = true;
        setGenerating(true);
        const signOutBar = document.querySelector('.sign-out-bar');
        if (signOutBar) signOutBar.style.display = 'none';

        const headerRight = targetRef.current.querySelector('.report-header-right');
        const headerLeft = targetRef.current.querySelector('.report-header-left');
        if (headerRight) headerRight.style.visibility = 'hidden';
        if (headerLeft) headerLeft.style.visibility = 'hidden';

        // One-line basis footer, present in the EXPORT only. The PDF is the artifact
        // most likely to be forwarded, so it states on its face what the headline
        // figure is (opportunity identified for review) and is not (a confirmed
        // billable amount), plus whether anything was excluded from it. The text is
        // computed once, in ImpactSummary, and read off data-pdf-basis — a clean
        // estimate has no panel and therefore no footer, which is correct.
        const basisText = targetRef.current
            .querySelector('.impact-summary-panel[data-pdf-basis]')?.getAttribute('data-pdf-basis') || '';
        let basisEl = null;
        if (basisText) {
            basisEl = document.createElement('div');
            basisEl.className = 'pdf-basis-footer';
            basisEl.textContent = basisText;
            basisEl.style.cssText = 'margin-top:18px;padding-top:12px;border-top:1px solid #e5e7eb;'
                + 'font-size:10px;line-height:1.5;color:#6b7280;';
            targetRef.current.appendChild(basisEl);
        }

        applyLightTheme();
        await new Promise(resolve => setTimeout(resolve, 150));

        const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 40;
        const contentWidth = pageWidth - margin * 2;

        const fullCanvas = await html2canvas(targetRef.current, {
            useCORS: true,
            scale: 2,
            backgroundColor: '#ffffff',
        });

        const pxPerPt = fullCanvas.width / contentWidth;
        const pageHeightPx = (pageHeight - margin * 2) * pxPerPt;

        const cards = targetRef.current.querySelectorAll(
            '.report-header, .impact-summary-panel, .clean-estimate-hero, .report-section, .no-findings, .pdf-basis-footer'
        );
        const containerRect = targetRef.current.getBoundingClientRect();
        const scaleX = fullCanvas.width / targetRef.current.offsetWidth;

        const safeBreaks = [0];
        cards.forEach(card => {
            const cardTop = (card.getBoundingClientRect().top - containerRect.top + window.scrollY) * scaleX;
            safeBreaks.push(Math.floor(cardTop));
        });
        safeBreaks.push(fullCanvas.height);
        safeBreaks.sort((a, b) => a - b);

        let sliceStart = 0;
        let isFirstPage = true;

        while (sliceStart < fullCanvas.height) {
            const maxEnd = sliceStart + pageHeightPx;
            let breakAt = maxEnd;
            for (const bp of safeBreaks) {
                if (bp > sliceStart && bp <= maxEnd) breakAt = bp;
            }
            if (breakAt <= sliceStart) breakAt = Math.min(sliceStart + pageHeightPx, fullCanvas.height);

            const sliceHeight = Math.min(breakAt - sliceStart, fullCanvas.height - sliceStart);
            if (sliceHeight < pageHeightPx * 0.05) break;

            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = fullCanvas.width;
            sliceCanvas.height = sliceHeight;
            const ctx = sliceCanvas.getContext('2d');
            ctx.drawImage(fullCanvas, 0, sliceStart, fullCanvas.width, sliceHeight, 0, 0, fullCanvas.width, sliceHeight);

            const imgData = sliceCanvas.toDataURL('image/jpeg', 1.0);
            const imgHeight = sliceHeight / pxPerPt;

            if (!isFirstPage) pdf.addPage();
            pdf.addImage(imgData, 'JPEG', margin, margin, contentWidth, imgHeight);
            isFirstPage = false;
            sliceStart = breakAt;
        }

        // Get export count from backend
        let exportCount = 1;
        try {
            const countRes = await fetch('https://tm-audit-platform-production.up.railway.app/api/export-count', {
                method: 'POST',
                headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    job_name: jobName,
                    // Migration 0033. job_name is not a usable join key — it truncates, it
                    // picks up job-code prefixes, and it has already drifted between the
                    // two tables on 7 of the first 34 exports. Send the id when we have it.
                    audit_id: auditId || currentAuditId(),
                }),
            });
            const countData = await countRes.json();
            exportCount = countData.count || 1;
        } catch (e) {
            console.warn('Export count failed, defaulting to 1');
        }
        const safeFileName = jobName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() + '.auditreport.' + exportCount + '.pdf';
        pdf.setProperties({ title: jobName + ' Audit Report' });
        const blob = pdf.output('blob');
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = safeFileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        restoreDarkTheme();
        if (basisEl && basisEl.parentNode) basisEl.parentNode.removeChild(basisEl);
        if (headerRight) headerRight.style.visibility = '';
        if (headerLeft) headerLeft.style.visibility = '';
        if (signOutBar) signOutBar.style.display = '';
        setGenerating(false);
    };


    return (
        // Objective 4: one provider supplies outcome capture to every FindingCard,
        // instead of threading auditId/outcomes/save through six call sites.
        <OutcomeContext.Provider value={{ auditId, outcomes, saveOutcome, busy: outcomeBusy }}>
        <DensityContext.Provider value={density}>
        <div className="report-screen" data-theme="light" ref={targetRef}>
            <div className="report-header">
                <div className="report-header-left">
                    {!generating && (
                        <button className="action-btn secondary" onClick={onReset}>New audit</button>
                    )}
                </div>
                <div className="report-header-center">
                    <div className="report-title">
                        <button
                            type="button"
                            className="audee-logo-home-button"
                            onClick={() => !generating && onReset()}
                            title="Back to Home"
                        >Audee<span className="logo-dot">.</span></button>
                        {parsedData.documentType?.startsWith('Xactimate') && <span className="report-type-badge xactimate-badge">Xactimate</span>}
                        {isCotality && <span className="report-type-badge cotality-badge">Cotality</span>}
                        {parsedData.documentType && !parsedData.documentType.startsWith('Xactimate') && !isCotality && <span className="report-type-badge tm-badge">T&M</span>}
                    </div>
                    <div className="report-subtitle">
                        {jobName && jobName !== 'Audit Report' ? jobName : ''}
                        {jobName && jobName !== 'Audit Report' && displayTotal ? ' · ' : ''}
                        {displayTotal
                            ? `$${parseFloat(String(displayTotal).replace(/[$,]/g, '')).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                            : ''}
                    </div>
                    {parsedData.documentType?.startsWith('Xactimate') && propertyTypeText(parsedData) && (
                        <div className="report-property-type">Property type: {propertyTypeText(parsedData)}</div>
                    )}
                </div>
                <div className="report-header-right">
                    {!generating && (
                        <button className="action-btn" onClick={handleDownloadPDF}>Download PDF</button>
                    )}
                    {generating && (
                        <button className="action-btn" disabled>Generating…</button>
                    )}
                </div>
            </div>

            {parsedData.documentType?.startsWith('Xactimate') && (
                <OccupancyQuestion parsedData={parsedData} onSetOccupancy={onSetOccupancy} />
            )}

            {/* Image-based ingestion disclosure. Shown only when the estimate was
                read from page images via OCR. HIGH confidence gets a subtle notice;
                REVIEW gets a stronger "verify some values" caution. Native-text
                audits render nothing here. */}
            {results.extraction?.sourceType === 'ocr' && (
                <div className={`ocr-disclosure ${results.extraction.status === 'REVIEW' ? 'review' : 'info'}`}>
                    {results.extraction.status === 'REVIEW'
                        ? <><Icon name="alert-triangle" size={15} /> This estimate was read from image-based pages. Some values may need review — please confirm the totals against your source document.</>
                        : <>This estimate was read from image-based pages and its totals were verified.</>}
                </div>
            )}

            {/* Under-read is ORTHOGONAL to whether findings exist. This block used to
                key on findings.length, so a single surviving finding hid the warning
                entirely and a document Audee never read rendered as a successful
                audit ("1 opportunity identified", green hero). Observed on
                doc:f2a812bb (audit 740): 0 line items
                extracted, readQuality.underRead true, and no warning shown because
                one non-absence finding survived.

                Two fixes here:
                  1. Decide the hero on ACTIVE findings, not the raw array. Suppressed
                     findings (see backend/extraction-gate.js) are not shown to the
                     customer, so a report whose findings are all suppressed must fall
                     through to the under-read / clean hero rather than render an
                     empty summary.
                  2. When findings DO survive on an under-read document, show the
                     warning above them instead of silently dropping it. */}
            {isCotality && <CotalityParseNotice parsedData={parsedData} />}

            {customerVisibleFindings.length === 0
                ? (isUnderRead(parsedData)
                    ? <UnderReadHero parsedData={parsedData} />
                    : <CleanEstimateHero parsedData={parsedData} />)
                : (<>
                    {isUnderRead(parsedData) && <UnderReadNotice parsedData={parsedData} />}
                    {isCotality
                        ? <CotalitySummary findings={findings} dismissedIds={dismissedIds} />
                        : <ImpactSummary findings={findings} dismissedIds={dismissedIds} parsedData={parsedData} auditId={auditId} />}
                </>)}

            {customerVisibleFindings.length === 0 && <EstimateOverview parsedData={parsedData} />}

            {/* Line-item notes renders on EVERY Xactimate report, independent of
                findings and of the Audit details toggle — exactly once: here on the
                clean path, or after the Audit details block when findings are shown.
                Keyed on customerVisibleFindings (not findings.length) so an audit whose
                findings are all suppressed, which shows the clean hero AND the details
                block, still gets exactly one. Not in the PDF export: it is an
                operator's fix-before-submitting list. Its own gates (notesVersion >= 2,
                under-read, absent notes field) live in LineItemNotesChecklist. */}
            {!generating && customerVisibleFindings.length === 0 && <LineItemNotesChecklist parsedData={parsedData} />}

            {hasDetails && (
                generating ? (
                    // During PDF export the toggle is meaningless (everything is
                    // expanded), so render a plain section header instead.
                    <div className="audit-details-bar">
                        <span className="audit-details-title">Audit details</span>
                    </div>
                ) : (
                    <button
                        className={`audit-details-cta ${detailsOpen ? 'open' : ''}`}
                        onClick={() => setDetailsOpen(o => !o)}
                        aria-expanded={detailsOpen}
                    >
                        <span className="audit-details-cta-text">
                            <span className="audit-details-cta-title">
                                {detailsOpen ? 'Audit details' : 'Show audit details'}
                            </span>
                            {!detailsOpen && (
                                <span className="audit-details-cta-sub">
                                    View findings, evidence, and recommendations.
                                </span>
                            )}
                        </span>
                        <span className="audit-details-cta-chevron">
                            <Icon name={detailsOpen ? 'chevron-up' : 'chevron-down'} size={16} />
                        </span>
                    </button>
                )
            )}

            {hasDetails && showDetails && (
            <>
            {!generating && (
                <TopOpportunities findings={findings} dismissedIds={dismissedIds} onJump={handleJumpToFinding} />
            )}

            {/* Density toggle sits directly below Top 5 (which it doesn't affect)
                and immediately above the finding list whose card density it
                actually controls, so flipping it changes something on-screen. */}
            {!generating && (
                <div className="density-bar">
                    <span className="density-bar-label">View</span>
                    <div className="density-toggle" role="group" aria-label="Report detail level">
                        <button
                            className={`density-btn ${density === 'simple' ? 'active' : ''}`}
                            onClick={() => setDensity('simple')}
                            aria-pressed={density === 'simple'}
                        >
                            Simple
                        </button>
                        <button
                            className={`density-btn ${density === 'pro' ? 'active' : ''}`}
                            onClick={() => setDensity('pro')}
                            aria-pressed={density === 'pro'}
                        >
                            Pro
                        </button>
                    </div>
                    <span className="density-bar-hint">
                        {density === 'simple'
                            ? 'Plain-English summary. Tap “Details” on any card for the technical view.'
                            : 'Full technical detail — rule IDs, scores, evidence, and monetization basis.'}
                    </span>
                </div>
            )}

            {!generating && hasRoomContext && (
                <div className="view-mode-bar">
                    <span className="view-mode-label">View</span>
                    <button
                        className={`view-mode-btn ${viewMode === 'severity' ? 'active' : ''}`}
                        onClick={() => setViewMode('severity')}
                    >
                        By severity
                    </button>
                    <button
                        className={`view-mode-btn ${viewMode === 'room' ? 'active' : ''}`}
                        onClick={() => setViewMode('room')}
                    >
                        By room
                    </button>
                </div>
            )}

            {!generating && hasDetails && (
                <div className="severity-filter-bar">
                    {['All', 'Critical', 'High', 'Medium', 'Low'].map(sev => {
                        const count = sev === 'All'
                            ? activeFindings.filter(f => !isSuppressed(f)).length
                            : activeFindings.filter(f => f.severity === sev && !isSuppressed(f)).length;
                        return (
                            <button
                                key={sev}
                                className={`sev-filter-btn sev-filter-${sev.toLowerCase()} ${severityFilter === sev ? 'active' : ''}`}
                                onClick={() => setSeverityFilter(sev)}
                            >
                                {sev} {count > 0 && <span className="sev-filter-count">{count}</span>}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* SECTION ORDER — Potential additions LEADS (2026-08-17, restored
                2026-08-28 after a three-day reversal).

                2026-08-25 moved additions last and collapsed them, on the reasoning
                that Opportunity is 89% of findings at avg confidence 59 while
                Requires Correction is 2.8% at avg confidence 84, and that the wedge
                is defensibility rather than recovery.

                RESTORED by founder decision 2026-08-28. Two things the 25 Aug call
                did not have:
                  · Measured on 68 recent Xactimate documents, 66% have an EMPTY
                    Requires Correction tier. For two operators in three the report
                    opened on an empty section with every finding collapsed below it.
                  · Opportunity carries 878 of 984 customer-visible findings and
                    averages $17,013 per document. Founder: additions "are the sexy
                    thing people are going to want to see as soon as they run an
                    audit," and a stack of $300 items "can really add up."

                Order is Potential additions → Fix before sending → Prepare to
                explain, on screen and therefore in the PDF (an html2canvas capture of
                this same DOM). Nothing about WHICH findings land in each section has
                changed — only their order, and that nothing starts collapsed. */}
            {viewMode === 'severity' && (
                <OpportunitiesSection
                    opportunities={filteredFindings
                        .filter(f => f.report_section === 'Opportunity' && !isSuppressed(f))
                        .sort((a, b) => (b.dollar_impact || 0) - (a.dollar_impact || 0))}
                    onDismiss={handleDismiss}
                    onRestore={handleRestore}
                    generating={generating}
                    totalBeforeFilter={unfilteredCount('Opportunity')}
                    isTmDocument={isTmDocument}
                    grandTotal={parsedData?.grandTotal ?? parsedData?.invoiceGrandTotal ?? null}
                    lumpSum={parsedData?.lumpSumContext || null}
                    isCotality={isCotality}
                />
            )}

            {viewMode === 'severity' && (
                <div className="report-section">
                    <div className="section-header correction">
                        <span className="section-icon"><Icon name="wrench" size={20} /></span>
                        Fix before sending
                        <span className="section-count">{isCotality ? cotalityCountText(requiresCorrection) : requiresCorrection.length}</span>
                    </div>
                    {requiresCorrection.length === 0 ? (
                        <EmptyTierNote filtered={unfilteredCount('Requires Correction') > 0}>
                            {parseWarning
                                ? <b>No corrections found in the lines Audee could read.</b>
                                : <><b>No corrections required.</b>{' '}
                                    Nothing in this estimate looks likely to be sent back by a desk
                                    adjuster. Most estimates land here.</>}
                        </EmptyTierNote>
                    ) : (
                        <div className="findings-list">
                            <FindingCards list={requiresCorrection} onDismiss={handleDismiss} onRestore={handleRestore} dismissed={false} generating={generating} />
                        </div>
                    )}
                </div>
            )}

            {/* Prepare to Explain — the contractor's DEFENSIVE section. These are the
                documentation gaps and line items an adjuster is most likely to challenge
                or cut; the contractor must fix them, or be ready to justify them, BEFORE
                the estimate goes out. They are deliberately distinct from Opportunity and
                Requires Correction, and their dollars are never in the revenue totals (see
                ImpactSummary: explainImpact is computed separately, never added to
                totalImpact). Suppressed findings are already excluded from prepareToExplain.
                On the audience='adjuster' reading these findings carry, see FindingCard. */}
            {viewMode === 'severity' && (
                <div className="report-section">
                    <div className="section-header explain">
                        <span className="section-icon"><Icon name="shield" size={20} /></span>
                        Prepare to explain
                        <span className="section-count">{isCotality ? cotalityCountText(prepareToExplain) : prepareToExplain.length}</span>
                    </div>
                    {prepareToExplain.length === 0 ? (
                        <EmptyTierNote filtered={unfilteredCount('Prepare to Explain') > 0}>
                            {parseWarning
                                ? <b>Nothing flagged in the lines Audee could read.</b>
                                : <><b>Nothing to prepare for.</b>{' '}
                                    No line items on this estimate look likely to draw a question in review.</>}
                        </EmptyTierNote>
                    ) : (
                        <>
                            <div className="section-intro">
                                Fix these or be ready to justify them before the estimate goes out — they're the documentation gaps and line items an adjuster is most likely to challenge. Not revenue, and not counted in any total above.
                            </div>
                            <div className="findings-list">
                                <FindingCards list={prepareToExplain} onDismiss={handleDismiss} onRestore={handleRestore} dismissed={false} generating={generating} />
                            </div>
                        </>
                    )}
                </div>
            )}

            {viewMode === 'room' && hasRoomContext && (
                <div className="report-section">
                    <div className="room-group-list">
                        {groupFindingsByRoom(filteredFindings.filter(f => !isSuppressed(f))).map(([label, groupFindings]) => {
                            const isRoom = !ROOM_GROUP_DISPLAY[label];
                            return (
                                <div className={`room-group${isRoom ? '' : ' room-group-wide'}`} key={label}>
                                    <div className="room-group-header">
                                        <span className="room-group-name">
                                            {isRoom && <span className="room-group-icon"><Icon name="home" size={15} /></span>}
                                            {isRoom ? label : ROOM_GROUP_DISPLAY[label]}
                                        </span>
                                        <span className="room-group-count">{groupFindings.length}</span>
                                    </div>
                                    {!isRoom && (
                                        <div className="room-group-caption">{ROOM_GROUP_CAPTION[label]}</div>
                                    )}
                                    <div className="findings-list">
                                        {groupFindings
                                            .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity])
                                            .map(f => (
                                                <FindingCard key={cardKey(f)} finding={f} onDismiss={handleDismiss} onRestore={handleRestore} dismissed={false} generating={generating} />
                                            ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {suppressedFindings.length > 0 && (
                <div className="report-section suppressed-section">
                    <div className="section-header suppressed" onClick={() => !generating && setSuppressedOpen(o => !o)} style={{ cursor: generating ? 'default' : 'pointer' }}>
                        <span className="section-icon"><Icon name="circle-dashed" size={20} /></span>
                        Suppressed / reviewer notes
                        <span className="section-count">{suppressedFindings.length}</span>
                        {!generating && <span className="section-chevron"><Icon name={suppressedOpen ? 'chevron-up' : 'chevron-down'} size={14} /></span>}
                    </div>
                    {(suppressedOpen || generating) && (
                        <>
                            <div className="suppressed-section-note">
                                Audee set these aside — they don't apply to this estimate's scope or occupancy and are not counted in any total above. Shown for reviewer reference only.
                            </div>
                            <div className="findings-list">
                                {suppressedFindings.map(f => (
                                    <FindingCard key={cardKey(f)} finding={f} onDismiss={handleDismiss} onRestore={handleRestore} dismissed={false} generating={generating} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {dismissedFindings.length > 0 && (
                <div className="report-section">
                    <div className="section-header reviewed" onClick={() => !generating && setReviewedOpen(o => !o)} style={{ cursor: generating ? 'default' : 'pointer' }}>
                        <span className="section-icon"><Icon name="check-circle" size={20} /></span>
                        Reviewed
                        <span className="section-count">{dismissedFindings.length}</span>
                        {!generating && <span className="section-chevron"><Icon name={reviewedOpen ? 'chevron-up' : 'chevron-down'} size={14} /></span>}
                    </div>
                    {reviewedOpen && (
                        <div className="findings-list">
                            <FindingCards list={dismissedFindings} onDismiss={handleDismiss} onRestore={handleRestore} dismissed={true} generating={generating} />
                        </div>
                    )}
                </div>
            )}

            </>
            )}

            {isCotality && <CotalityNotEvaluated parsedData={parsedData} generating={generating} />}

            {/* Line-item notes, findings path: directly after the Audit details block,
                outside it, so collapsing Audit details does not hide it. */}
            {!generating && customerVisibleFindings.length > 0 && <LineItemNotesChecklist parsedData={parsedData} />}

            {/* Internal Feedback Center — placed AFTER the findings so the report
                reads Summary → Details → Feedback. The "Did this audit help?"
                strip no longer blocks the initial view. Additive only; hidden
                during PDF generation. */}
            {!generating && (
                <FeedbackWidget
                    results={results}
                    fileName={fileName}
                    jobName={jobName}
                    session={session}
                    getRuntime={getFeedbackRuntime}
                />
            )}
        </div>
        </DensityContext.Provider>
        </OutcomeContext.Provider>
    );
}

export default AuditReport;